import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ExploreUnknownGridOverlay from './ExploreUnknownGridOverlay';
import ExploreUnknownArrivalPointsOverlay from './ExploreUnknownArrivalPointsOverlay';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ExploreUnknown.css';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { stopAllAudio } from './hooks/musicState';
import { playSoundEffect } from './hooks/useSoundEffects';
import { isExploreUnknownKendrexColonySurface } from './config/exploreUnknownKendrexColony';
import { setExploreUnknownKendrexColonyMapAudioSuppressed } from './utils/exploreUnknownKendrexColonyAudio';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';
import { useContainerScale } from './hooks/useViewportScale';
import { EXPLORE_UNKNOWN_PORTAL_LAYOUT } from './exploreUnknownPortalLayout';
import { EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS } from './config/exploreUnknownDarkKnowledge';
import {
  EXPLORE_UNKNOWN_HEART_METERS,
  EXPLORE_UNKNOWN_HEART_TARGET_PARTS,
} from './config/exploreUnknownHearts';
import {
  EXPLORE_UNKNOWN_EP_METERS,
  EXPLORE_UNKNOWN_EP_TARGET_PARTS,
} from './config/exploreUnknownEmergencyProtocol';
import {
  EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER,
  EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS,
} from './config/exploreUnknownRewriteExperience';
import { ExploreUnknownHeartProgressBar } from './components/ExploreUnknownHeartProgressBar';
import { computeMapSlicePointStates } from './utils/exploreUnknownMapSlice';
import { exploreUnknownXpBarPercent } from './utils/exploreUnknownXpBarPercent';
import ExploreUnknownLevelTree from './ExploreUnknownLevelTree';
import ExploreUnknownSkipWorldDialog from './ExploreUnknownSkipWorldDialog';
import ExploreUnknownDevExperiencePanel from './ExploreUnknownDevExperiencePanel';
import { API_URL, WS_URL, getWebSocketAuthMessage } from './config/apiConfig';
import { needsTokenRefresh, refreshToken, isTokenValid } from './auth';
import CustomDropdown from './CustomDropdown';
import { normalizeStrategyValidationResponse } from './utils/normalizeStrategyValidationResponse';
import { TutorialMessageBox } from './tutorial/index';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import {
  loadDevWaypoints,
  saveDevWaypoints,
  clearDevWaypoints,
  getExpectedWaypointCount,
} from './utils/exploreUnknownWaypointDevStorage';
import riteOfRespiteModalBg from './assets/images/rite_of_respite.jpg';
import { useExploreUnknownMinigameSurface } from './exploreUnknownMinigames/useExploreUnknownMinigameSurface';
import ExploreUnknownMinigameHost from './exploreUnknownMinigames/ExploreUnknownMinigameHost';

function getStrategyOrderCount(strategy) {
  if (!strategy || !Array.isArray(strategy.orders)) return 0;
  return strategy.orders.reduce((total, order) => total + (order.count || 0), 0);
}

function isValidOrderCount(strategy) {
  const orderCount = getStrategyOrderCount(strategy);
  return orderCount >= 50 && orderCount <= 100;
}

/** @param {unknown} raw */
function parseExploreUnknownDarknessPercent(raw) {
  if (raw === undefined || raw === null) {
    throw new Error('Explore Unknown API response missing darknessPercent');
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n > 100) {
    throw new Error(`Explore Unknown: invalid darknessPercent (${String(raw)})`);
  }
  return Math.round(n);
}

/** @param {unknown} raw */
function parseExploreUnknownPlayerStartingHealth(raw) {
  if (raw === undefined || raw === null) {
    throw new Error('Explore Unknown API response missing playerStartingHealth');
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 5) {
    throw new Error(`Explore Unknown: invalid playerStartingHealth (${String(raw)})`);
  }
  return Math.round(n);
}

/** @param {unknown} raw */
function parseExploreUnknownBargainUsed(raw) {
  if (raw !== true && raw !== false) {
    throw new Error(`Explore Unknown API: invalid darkness bargain used flag (${String(raw)})`);
  }
  return raw;
}

/** @param {unknown} raw */
function parseExploreUnknownDarkKnowledgePartsCollected(raw) {
  if (raw === undefined || raw === null) {
    throw new Error('Explore Unknown API response missing darkKnowledgePartsCollected');
  }
  const n = Number(raw);
  if (
    !Number.isFinite(n) ||
    !Number.isInteger(n) ||
    n < 0 ||
    n > EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS
  ) {
    throw new Error(`Explore Unknown: invalid darkKnowledgePartsCollected (${String(raw)})`);
  }
  return n;
}

/** @param {unknown} raw */
function parseExploreUnknownHeartPartsCollected(raw, fieldName) {
  if (raw === undefined || raw === null) {
    throw new Error(`Explore Unknown API response missing ${fieldName}`);
  }
  const n = Number(raw);
  if (
    !Number.isFinite(n) ||
    !Number.isInteger(n) ||
    n < 0 ||
    n > EXPLORE_UNKNOWN_HEART_TARGET_PARTS
  ) {
    throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
  }
  return n;
}

/** @param {unknown} raw */
function parseExploreUnknownHeartSetsCompleted(raw, fieldName) {
  if (raw === undefined || raw === null) {
    throw new Error(`Explore Unknown API response missing ${fieldName}`);
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
    throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
  }
  return n;
}

/** @param {Record<string, unknown>} data */
function readHeartRunFromPayload(data) {
  return {
    weakHeartPartsCollected: parseExploreUnknownHeartPartsCollected(
      data.weakHeartPartsCollected,
      'weakHeartPartsCollected'
    ),
    heartPartsCollected: parseExploreUnknownHeartPartsCollected(
      data.heartPartsCollected,
      'heartPartsCollected'
    ),
    strongHeartPartsCollected: parseExploreUnknownHeartPartsCollected(
      data.strongHeartPartsCollected,
      'strongHeartPartsCollected'
    ),
    weakHeartSetsCompleted: parseExploreUnknownHeartSetsCompleted(
      data.weakHeartSetsCompleted,
      'weakHeartSetsCompleted'
    ),
    heartSetsCompleted: parseExploreUnknownHeartSetsCompleted(
      data.heartSetsCompleted,
      'heartSetsCompleted'
    ),
    strongHeartSetsCompleted: parseExploreUnknownHeartSetsCompleted(
      data.strongHeartSetsCompleted,
      'strongHeartSetsCompleted'
    ),
  };
}

/** @param {Record<string, unknown>} data */
function readEpRunFromPayload(data) {
  const parseParts = (raw, fieldName) => {
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > EXPLORE_UNKNOWN_EP_TARGET_PARTS - 1) {
      throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
    }
    return n;
  };
  const parseCharges = (raw, fieldName) => {
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
    }
    return n;
  };
  return {
    epGlobalPartsCollected: parseParts(data.epGlobalPartsCollected, 'epGlobalPartsCollected'),
    epGlobalCharges: parseCharges(data.epGlobalCharges, 'epGlobalCharges'),
    epZonePartsCollected: parseParts(data.epZonePartsCollected, 'epZonePartsCollected'),
    epZoneCharges: parseCharges(data.epZoneCharges, 'epZoneCharges'),
    epTargetPartsCollected: parseParts(data.epTargetPartsCollected, 'epTargetPartsCollected'),
    epTargetCharges: parseCharges(data.epTargetCharges, 'epTargetCharges'),
  };
}

function readRewriteExperienceRunFromPayload(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('readRewriteExperienceRunFromPayload: data is required');
  }
  const parseParts = (raw, fieldName) => {
    const n = Number(raw);
    if (
      !Number.isFinite(n) ||
      !Number.isInteger(n) ||
      n < 0 ||
      n > EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS - 1
    ) {
      throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
    }
    return n;
  };
  const parseCharges = (raw, fieldName) => {
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      throw new Error(`Explore Unknown: invalid ${fieldName} (${String(raw)})`);
    }
    return n;
  };
  return {
    rewriteExperiencePartsCollected: parseParts(
      data.rewriteExperiencePartsCollected,
      'rewriteExperiencePartsCollected'
    ),
    rewriteExperienceCharges: parseCharges(
      data.rewriteExperienceCharges,
      'rewriteExperienceCharges'
    ),
  };
}

/** Current map surface → API tier key (milestones 3, 7, 14, 19, 25). */
const SURFACE_TO_DARKNESS_BARGAIN_TIER = {
  arrival: 'm3',
  cave: 'm6',
  desert: 'm11',
  fair: 'm19',
  lavender: 'm25',
  kendrex: 'm42',
};

/** Must match `DARKNESS_BARGAIN_MIN_PRIOR_HEALTH` on server (5 + 5). */
const EXPLORE_UNKNOWN_BARGAIN_MIN_PRIOR_HEALTH = 10;

/** Chapter order for +5% darkness when advancing to a deeper map (`POST /explore-unknown/continue-surface`). */
const EXPLORE_SURFACE_DARKNESS_TIER = Object.freeze({
  arrival: 0,
  cave: 1,
  desert: 2,
  fair: 3,
  lavender: 4,
  treetop: 5,
  boat: 6,
  pixelpast: 7,
  kendrex: 8,
});

const EXPLORE_SURFACE_CHAIN = Object.freeze([
  'arrival',
  'cave',
  'desert',
  'fair',
  'lavender',
  'treetop',
  'boat',
  'pixelpast',
  'kendrex',
]);

/** Surface keys → `exploreUnknownPage.*` map title keys (aligned with `exploreUnknownPortalLayout.js`). */
const EXPLORE_UNKNOWN_MAP_NAME_I18N_KEYS = Object.freeze({
  arrival: 'mapNameArrival',
  cave: 'mapNameCave',
  desert: 'mapNameDesert',
  fair: 'mapNameFair',
  lavender: 'mapNameLavender',
  treetop: 'mapNameTreetop',
  boat: 'mapNameBoat',
  pixelpast: 'mapNamePixelPast',
  kendrex: 'mapNameKendrex',
});

/**
 * @param {string} from
 * @param {string} to
 * @returns {boolean}
 */
function exploreSurfaceChargesDarkness(from, to) {
  const a = EXPLORE_SURFACE_DARKNESS_TIER[from];
  const b = EXPLORE_SURFACE_DARKNESS_TIER[to];
  if (a === undefined || b === undefined) {
    throw new Error(`Explore Unknown: invalid surface for darkness tier (${String(from)} → ${String(to)})`);
  }
  return b > a;
}

const EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY = 'exploreUnknownSurface';
const EXPLORE_UNKNOWN_CAVE_EXIT_DISMISSED_KEY = 'exploreUnknownCaveExitDismissed';
const STRATEGY_VALIDATION_TIMEOUT_MS = 12000;

/** Shift strategy label + dropdown left on the portal screen, in real viewport pixels (layout is under `scale(viewportScale)`). */
const EXPLORE_UNKNOWN_STRATEGY_START_SHIFT_SCREEN_PX = 40;

const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500',
};

const factionIconByName = {
  'Remnants of Humanity': 'remnants.png',
  'Exolot Dominion': 'exolot.png',
  'League of Shadows': 'league.png',
  'Ra\'s Children': 'ra.png',
  'Asrah': 'asrah.png',
  'The Conspiracy': 'conspiracy.png',
  'The Corrupted Core': 'core.png',
  'The Covenant': 'covenant.png',
  'The Divine Syndicate': 'syndicate.png',
  'The Forsaken Pact': 'forsaken.png',
  'The Hollow': 'hollow.png',
  'The Iron Alliance': 'alliance.png',
  'The Last Crusade': 'crusade.png',
  'The Shadow Consortium': 'consortium.png',
  'The Silent Accord': 'silent.png',
};

const getFactionOrbBackground = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  return factionColors[factionName] || '#ccc';
};

const getFactionIconPath = (factionName) => {
  if (!factionName || factionName === 'Unknown' || factionName === 'The Sentients') {
    return null;
  }

  const iconFile = factionIconByName[factionName];
  if (!iconFile) {
    throw new Error(`Explore Unknown faction icon mapping missing for faction: ${factionName}`);
  }

  return `${process.env.PUBLIC_URL}/factions/${iconFile}`;
};

function readExploreSurfaceFromStorage() {
  try {
    const s = sessionStorage.getItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
    if (
      s === 'cave' ||
      s === 'arrival' ||
      s === 'desert' ||
      s === 'fair' ||
      s === 'lavender' ||
      s === 'treetop' ||
      s === 'boat' ||
      s === 'pixelpast' ||
      s === 'kendrex'
    ) {
      return s;
    }
  } catch {
    /* ignore */
  }
  return 'arrival';
}

/** Desktop-style hover + fine pointer: portal intro can drive background video play/pause. */
function readPortalBackgroundVideoHoverMode() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/**
 * @param {{ wins?: number, losses?: number, attempts?: number, winRatePercent?: number | null } | null | undefined} entry
 * @param {(key: string, opts?: object) => string} t
 */
function formatExploreUnknownCompletionRateValue(entry, t) {
  if (!entry || !Number(entry.attempts) || entry.winRatePercent == null) {
    return t('exploreUnknownPage.missionCompletionRateNoData');
  }
  return t('exploreUnknownPage.missionCompletionRatePercent', { percent: entry.winRatePercent });
}

async function fetchJsonWithTimeout(url, options, timeoutMs) {
  const ac = new AbortController();
  const timeoutId = window.setTimeout(() => ac.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: ac.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function ExploreUnknown({ user, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    scale: viewportScale,
    offsetX: viewportOffsetX,
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile,
  } = useContainerScale();

  const strategyStartBlockLogicalLeftPx = useMemo(() => {
    if (!(viewportScale > 0) || Number.isNaN(viewportScale)) {
      throw new Error('Explore Unknown: viewportScale must be a positive number');
    }
    return -EXPLORE_UNKNOWN_STRATEGY_START_SHIFT_SCREEN_PX / viewportScale;
  }, [viewportScale]);

  const backgroundVideoRef = useRef(null);
  /** Portal hitbox: pointer/focus listeners for hover-gated background video (fine pointer only). */
  const portalRegionRef = useRef(null);
  const exploreContainerRef = useRef(null);
  const scannerVideoRef = useRef(null);
  const strategyHintTimerRef = useRef(null);
  const resumeHandledRef = useRef(false);
  const mapAnimTimerRef = useRef(null);
  const pendingResumeRunIdRef = useRef(null);
  /** After map return animation: refetch run row so darkness / mission count match the server. */
  const mapRefreshWasAnimatingRef = useRef(false);

  const [strategies, setStrategies] = useState([]);
  const [strategiesError, setStrategiesError] = useState(null);
  const [strategyValidations, setStrategyValidations] = useState({});
  const [selectedStrategyId, setSelectedStrategyId] = useState('');
  /** Set when the player enters the portal; strategy choice is final. */
  const [committedStrategyId, setCommittedStrategyId] = useState(null);
  const [strategyHintVisible, setStrategyHintVisible] = useState(false);
  /** null | 'scanner' | 'arrival' */
  const [sequencePhase, setSequencePhase] = useState(null);
  const sequencePhaseRef = useRef(sequencePhase);
  sequencePhaseRef.current = sequencePhase;

  const [portalBackgroundVideoHoverMode, setPortalBackgroundVideoHoverMode] = useState(readPortalBackgroundVideoHoverMode);
  const [missionProgress, setMissionProgress] = useState(null);
  const [missionProgressError, setMissionProgressError] = useState(null);
  const [startingMission, setStartingMission] = useState(false);
  const [missionStartError, setMissionStartError] = useState(null);
  const [mapRefreshAnimating, setMapRefreshAnimating] = useState(false);
  const [exploreWs, setExploreWs] = useState(null);
  const [activeRunId, setActiveRunId] = useState(null);
  const [missionsCompletedInRun, setMissionsCompletedInRun] = useState(0);
  /** 0–100: darkness toward the eclipse; server adds % from human player turn count per won mission. */
  const [darknessPercent, setDarknessPercent] = useState(0);
  /** Fragments collected toward the Dark Knowledge legendary reward this run (0…target). */
  const [darkKnowledgePartsCollected, setDarkKnowledgePartsCollected] = useState(0);
  const [weakHeartPartsCollected, setWeakHeartPartsCollected] = useState(0);
  const [heartPartsCollected, setHeartPartsCollected] = useState(0);
  const [strongHeartPartsCollected, setStrongHeartPartsCollected] = useState(0);
  const [weakHeartSetsCompleted, setWeakHeartSetsCompleted] = useState(0);
  const [heartSetsCompleted, setHeartSetsCompleted] = useState(0);
  const [strongHeartSetsCompleted, setStrongHeartSetsCompleted] = useState(0);
  const [epGlobalPartsCollected, setEpGlobalPartsCollected] = useState(0);
  const [epGlobalCharges, setEpGlobalCharges] = useState(0);
  const [epZonePartsCollected, setEpZonePartsCollected] = useState(0);
  const [epZoneCharges, setEpZoneCharges] = useState(0);
  const [epTargetPartsCollected, setEpTargetPartsCollected] = useState(0);
  const [epTargetCharges, setEpTargetCharges] = useState(0);
  const [rewriteExperiencePartsCollected, setRewriteExperiencePartsCollected] = useState(0);
  const [rewriteExperienceCharges, setRewriteExperienceCharges] = useState(0);
  /** Player starting health for future EU missions this run (default 30; reduced by darkness bargain). */
  const [playerStartingHealth, setPlayerStartingHealth] = useState(30);
  const [darknessBargainM3Used, setDarknessBargainM3Used] = useState(false);
  const [darknessBargainM6Used, setDarknessBargainM6Used] = useState(false);
  const [darknessBargainM11Used, setDarknessBargainM11Used] = useState(false);
  const [darknessBargainM19Used, setDarknessBargainM19Used] = useState(false);
  const [darknessBargainM25Used, setDarknessBargainM25Used] = useState(false);
  const [darknessBargainM42Used, setDarknessBargainM42Used] = useState(false);
  const [darknessBargainModalTier, setDarknessBargainModalTier] = useState(null);
  const [darknessBargainPurchaseBusy, setDarknessBargainPurchaseBusy] = useState(false);
  const [darknessBargainPurchaseError, setDarknessBargainPurchaseError] = useState(null);
  const [nextMissionCompletionRates, setNextMissionCompletionRates] = useState(null);
  const [nextMissionCompletionRatesLoading, setNextMissionCompletionRatesLoading] = useState(false);
  const [nextMissionCompletionRatesError, setNextMissionCompletionRatesError] = useState(null);
  const [continueSurfaceBusy, setContinueSurfaceBusy] = useState(false);
  const [continueSurfaceError, setContinueSurfaceError] = useState(null);
  const [runSessionError, setRunSessionError] = useState(null);
  /** True when run id + mission count came from GET /explore-unknown/active-run (skip POST run-session). */
  const [runHydratedFromApi, setRunHydratedFromApi] = useState(false);
  /** False until we know whether to show intro (portal) or hydrate mid-run map. */
  const [pendingActiveRunCheck, setPendingActiveRunCheck] = useState(true);
  const [showTutorialExploreUnknown, setShowTutorialExploreUnknown] = useState(false);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);
  const [exploreUnknownLevel, setExploreUnknownLevel] = useState(1);
  const [exploreUnknownTotalXp, setExploreUnknownTotalXp] = useState(0);
  const [exploreUnknownXpIntoLevel, setExploreUnknownXpIntoLevel] = useState(0);
  const [exploreUnknownXpToNext, setExploreUnknownXpToNext] = useState(50);
  const [exploreUnknownMilestoneBonuses, setExploreUnknownMilestoneBonuses] = useState({});
  const [skipWorldDialog, setSkipWorldDialog] = useState(null);
  const [skipWorldBusy, setSkipWorldBusy] = useState(false);
  /** `arrival` 1–5 · `cave` 6–10 · `desert` 11–16 · `fair` 17–23 · `lavender` after fair (see `exploreUnknownPortalLayout.js`). */
  const [exploreSurface, setExploreSurface] = useState(readExploreSurfaceFromStorage);
  const exploreSurfaceRef = useRef(exploreSurface);
  exploreSurfaceRef.current = exploreSurface;

  const exploreUnknownAmbientMusicPath = useMemo(() => {
    if (isExploreUnknownKendrexColonySurface(exploreSurface)) {
      return null;
    }
    const onFairgroundsMapView = exploreSurface === 'fair' && sequencePhase === 'arrival';
    if (!onFairgroundsMapView) {
      return '/music/unknown.mp3';
    }
    return Math.random() < 0.5 ? '/music/fair1.mp3' : '/music/fair2.mp3';
  }, [exploreSurface, sequencePhase]);

  useMusic(exploreUnknownAmbientMusicPath);

  useEffect(() => {
    const suppressed = isExploreUnknownKendrexColonySurface(exploreSurface);
    setExploreUnknownKendrexColonyMapAudioSuppressed(suppressed);
    if (suppressed) {
      stopAllAudio();
    }
    return () => setExploreUnknownKendrexColonyMapAudioSuppressed(false);
  }, [exploreSurface]);

  const [devAutowinBusy, setDevAutowinBusy] = useState(false);
  const [devAutowinError, setDevAutowinError] = useState(null);
  const [devForceLossBusy, setDevForceLossBusy] = useState(false);
  const [devForceLossError, setDevForceLossError] = useState(null);
  /** Set from map `<img>` onLoad — aligns POI SVG with `object-fit: contain` (see ExploreUnknownArrivalPointsOverlay). */
  const [mapImageNaturalSize, setMapImageNaturalSize] = useState(null);
  const [caveExitDismissed, setCaveExitDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(EXPLORE_UNKNOWN_CAVE_EXIT_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const mapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.mapSliceMissionCount ?? 5;

  /** Global indices for the arrival map slice (e.g. 1–5). */
  const arrivalMissionIndices = useMemo(
    () => Array.from({ length: mapSliceMissionCount }, (_, i) => i + 1),
    [mapSliceMissionCount]
  );

  /** First global mission index on the desert map (after arrival + cave slices). */
  const desertFirstMissionIndex = useMemo(() => 2 * mapSliceMissionCount + 1, [mapSliceMissionCount]);

  /** Global mission indices for arrival + cave (e.g. 1–10) — used for the desert entrance gate. */
  const firstTwoSliceMissionIndices = useMemo(
    () => Array.from({ length: 2 * mapSliceMissionCount }, (_, i) => i + 1),
    [mapSliceMissionCount]
  );

  /** Global mission indices for the cave map slice (e.g. 6–10 when mapSliceMissionCount is 5). */
  const caveMissionIndices = useMemo(
    () =>
      Array.from({ length: mapSliceMissionCount }, (_, i) => mapSliceMissionCount + 1 + i),
    [mapSliceMissionCount]
  );

  const desertMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.desertMapSliceMissionCount ?? 6;
  const fairMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.fairMapSliceMissionCount ?? 7;
  const lavenderMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.lavenderMapSliceMissionCount ?? 5;
  const treetopMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.treetopMapSliceMissionCount ?? 1;
  const boatMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.boatMapSliceMissionCount ?? 5;
  const pixelPastMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.pixelPastMapSliceMissionCount ?? 6;
  const kendrexMapSliceMissionCount = EXPLORE_UNKNOWN_PORTAL_LAYOUT.kendrexMapSliceMissionCount ?? 6;

  /** Global mission indices on the fairgrounds map (e.g. 17–23). */
  const fairMissionIndices = useMemo(
    () =>
      Array.from({ length: fairMapSliceMissionCount }, (_, i) => desertFirstMissionIndex + desertMapSliceMissionCount + i),
    [fairMapSliceMissionCount, desertFirstMissionIndex, desertMapSliceMissionCount]
  );

  /** Global mission indices on the desert map (e.g. 11–16). */
  const desertMissionIndices = useMemo(
    () =>
      Array.from({ length: desertMapSliceMissionCount }, (_, i) => desertFirstMissionIndex + i),
    [desertMapSliceMissionCount, desertFirstMissionIndex]
  );

  /** Global mission indices on the lavender map (e.g. 24–28). */
  const lavenderMissionIndices = useMemo(
    () =>
      Array.from(
        { length: lavenderMapSliceMissionCount },
        (_, i) => desertFirstMissionIndex + desertMapSliceMissionCount + fairMapSliceMissionCount + i
      ),
    [lavenderMapSliceMissionCount, desertFirstMissionIndex, desertMapSliceMissionCount, fairMapSliceMissionCount]
  );

  /** Global mission indices on the treetop map (e.g. 29). */
  const treetopMissionIndices = useMemo(
    () =>
      Array.from(
        { length: treetopMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          i
      ),
    [
      treetopMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
    ]
  );

  /** Global mission indices on the boat map (e.g. 30–34). */
  const boatMissionIndices = useMemo(
    () =>
      Array.from(
        { length: boatMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          treetopMapSliceMissionCount +
          i
      ),
    [
      boatMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
      treetopMapSliceMissionCount,
    ]
  );

  /** Global mission indices on the Pixel Past map (e.g. 35–40). */
  const pixelPastMissionIndices = useMemo(
    () =>
      Array.from(
        { length: pixelPastMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          treetopMapSliceMissionCount +
          boatMapSliceMissionCount +
          i
      ),
    [
      pixelPastMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
      treetopMapSliceMissionCount,
      boatMapSliceMissionCount,
    ]
  );

  /** Global mission indices on the Kendrex Colony map (e.g. 41–46). */
  const kendrexMissionIndices = useMemo(
    () =>
      Array.from(
        { length: kendrexMapSliceMissionCount },
        (_, i) =>
          desertFirstMissionIndex +
          desertMapSliceMissionCount +
          fairMapSliceMissionCount +
          lavenderMapSliceMissionCount +
          treetopMapSliceMissionCount +
          boatMapSliceMissionCount +
          pixelPastMapSliceMissionCount +
          i
      ),
    [
      kendrexMapSliceMissionCount,
      desertFirstMissionIndex,
      desertMapSliceMissionCount,
      fairMapSliceMissionCount,
      lavenderMapSliceMissionCount,
      treetopMapSliceMissionCount,
      boatMapSliceMissionCount,
      pixelPastMapSliceMissionCount,
    ]
  );

  const hasEnteredPortal = committedStrategyId !== null;

  const strategyDropdownOptions = useMemo(
    () =>
      strategies.map((s) => {
        if (!s || s.id == null) {
          throw new Error('Explore Unknown strategy dropdown: strategy id is required.');
        }
        if (!s.faction_name) {
          throw new Error(`Explore Unknown strategy dropdown: strategy faction is required for strategy ${String(s.id)}.`);
        }
        return {
          value: String(s.id),
          label: s.name,
          factionName: s.faction_name,
        };
      }),
    [strategies]
  );

  const showExploreUnknownDevAutowin = useMemo(() => {
    if (process.env.NODE_ENV !== 'development') return false;
    return (
      (typeof user === 'string' && user === 'player1') ||
      (user != null && typeof user === 'object' && user.username === 'player1')
    );
  }, [user]);

  /** Account `player1` only: replay portal tutorial from the start screen without clearing seen state. */
  const showPlayer1PortalTutorialButton = useMemo(() => {
    return (
      (typeof user === 'string' && user === 'player1') ||
      (user != null && typeof user === 'object' && user.username === 'player1')
    );
  }, [user]);

  const showPlayer1MinigameDevGrid = showPlayer1PortalTutorialButton;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const sync = () => {
      setPortalBackgroundVideoHoverMode(Boolean(mq.matches));
    };
    sync();
    mq.addEventListener('change', sync);
    return () => {
      mq.removeEventListener('change', sync);
    };
  }, []);

  const showExploreUnknownDevWaypointEditor = process.env.NODE_ENV === 'development';

  /** Dev-only: overrides layout POIs from localStorage; null = use bundled layout. */
  const [devWaypointOverride, setDevWaypointOverride] = useState(null);
  const [waypointEditMode, setWaypointEditMode] = useState(false);
  const [devWaypointStorageError, setDevWaypointStorageError] = useState(null);
  const [devWaypointUiError, setDevWaypointUiError] = useState(null);
  const [waypointCopyDone, setWaypointCopyDone] = useState(false);

  useEffect(() => {
    if (!showExploreUnknownDevWaypointEditor) return;
    setWaypointEditMode(false);
    setDevWaypointStorageError(null);
    try {
      setDevWaypointOverride(loadDevWaypoints(exploreSurface));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const expectedLen = getExpectedWaypointCount(exploreSurface);
      if (msg.includes(`array of length ${expectedLen}`)) {
        // Waypoint count changed (e.g. lavender 4 -> 5): clear stale localStorage snapshot.
        try {
          clearDevWaypoints(exploreSurface);
          setDevWaypointOverride(null);
          setDevWaypointStorageError(null);
          return;
        } catch (clearErr) {
          const clearMsg = clearErr instanceof Error ? clearErr.message : String(clearErr);
          setDevWaypointStorageError(clearMsg);
          setDevWaypointOverride(null);
          return;
        }
      }
      setDevWaypointStorageError(msg);
      setDevWaypointOverride(null);
    }
  }, [exploreSurface, showExploreUnknownDevWaypointEditor]);

  const mapMissionOffset = useMemo(() => {
    if (exploreSurface === 'kendrex') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount
      );
    }
    if (exploreSurface === 'pixelpast') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount
      );
    }
    if (exploreSurface === 'boat') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount
      );
    }
    if (exploreSurface === 'treetop') {
      return (
        2 * mapSliceMissionCount +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount
      );
    }
    if (exploreSurface === 'lavender') {
      return 2 * mapSliceMissionCount + desertMapSliceMissionCount + fairMapSliceMissionCount;
    }
    if (exploreSurface === 'fair') {
      return 2 * mapSliceMissionCount + desertMapSliceMissionCount;
    }
    if (exploreSurface === 'desert') return 2 * mapSliceMissionCount;
    if (exploreSurface === 'cave') return mapSliceMissionCount;
    return 0;
  }, [
    exploreSurface,
    mapSliceMissionCount,
    desertMapSliceMissionCount,
    fairMapSliceMissionCount,
    lavenderMapSliceMissionCount,
    treetopMapSliceMissionCount,
    boatMapSliceMissionCount,
    pixelPastMapSliceMissionCount,
  ]);

  const layoutMapPoints = useMemo(() => {
    if (exploreSurface === 'kendrex') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.kendrexPointsOfInterest ?? [];
    }
    if (exploreSurface === 'pixelpast') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.pixelPastPointsOfInterest ?? [];
    }
    if (exploreSurface === 'boat') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.boatPointsOfInterest ?? [];
    }
    if (exploreSurface === 'treetop') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.treetopPointsOfInterest ?? [];
    }
    if (exploreSurface === 'lavender') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.lavenderPointsOfInterest ?? [];
    }
    if (exploreSurface === 'fair') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.fairPointsOfInterest ?? [];
    }
    if (exploreSurface === 'desert') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.desertPointsOfInterest;
    }
    if (exploreSurface === 'cave') {
      return EXPLORE_UNKNOWN_PORTAL_LAYOUT.cavePointsOfInterest;
    }
    return EXPLORE_UNKNOWN_PORTAL_LAYOUT.arrivalPointsOfInterest;
  }, [exploreSurface]);

  const currentMapPoints = useMemo(() => {
    if (showExploreUnknownDevWaypointEditor && devWaypointOverride != null) {
      return devWaypointOverride;
    }
    return layoutMapPoints;
  }, [showExploreUnknownDevWaypointEditor, devWaypointOverride, layoutMapPoints]);

  const slicePointStates = useMemo(() => {
    if (!missionProgress || !Array.isArray(currentMapPoints) || currentMapPoints.length < 1) return null;
    return computeMapSlicePointStates(
      missionProgress.completedIndices,
      missionProgress.nextAvailable,
      mapMissionOffset,
      currentMapPoints.length
    );
  }, [missionProgress, exploreSurface, mapMissionOffset, currentMapPoints]);

  /**
   * Without mission progress the real slice is null and the POI layer would not mount — dev waypoint
   * editing would be impossible. In development, fall back to all-unavailable so handles still render.
   */
  const effectiveSlicePointStates = useMemo(() => {
    if (slicePointStates != null) return slicePointStates;
    if (
      showExploreUnknownDevWaypointEditor &&
      Array.isArray(currentMapPoints) &&
      currentMapPoints.length > 0
    ) {
      return currentMapPoints.map(() => 'unavailable');
    }
    return null;
  }, [slicePointStates, showExploreUnknownDevWaypointEditor, currentMapPoints]);

  const nextSelectableMissionIndex = useMemo(() => {
    const idx = missionProgress?.nextAvailable;
    if (idx == null || typeof idx !== 'number' || !Number.isInteger(idx) || idx < 1) {
      return null;
    }
    return idx;
  }, [missionProgress]);

  const showCaveEntrancePrompt = useMemo(() => {
    if (exploreSurface !== 'arrival' || !missionProgress) return false;
    const { completedIndices, nextAvailable } = missionProgress;
    const doneFirstMap =
      Array.isArray(completedIndices) &&
      arrivalMissionIndices.every((i) => completedIndices.includes(i));
    return doneFirstMap && nextAvailable === mapSliceMissionCount + 1;
  }, [exploreSurface, missionProgress, arrivalMissionIndices, mapSliceMissionCount]);

  useEffect(() => {
    const ci = missionProgress?.completedIndices;
    if (!Array.isArray(ci)) return;
    const allCaveDone = caveMissionIndices.every((idx) => ci.includes(idx));
    if (!allCaveDone) {
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_CAVE_EXIT_DISMISSED_KEY);
      } catch {
        /* ignore */
      }
      setCaveExitDismissed(false);
    }
  }, [missionProgress, caveMissionIndices]);

  const showCaveExitPrompt = useMemo(() => {
    if (exploreSurface !== 'cave' || !missionProgress || caveExitDismissed) return false;
    const ci = missionProgress.completedIndices;
    if (!Array.isArray(ci)) return false;
    return caveMissionIndices.every((idx) => ci.includes(idx));
  }, [exploreSurface, missionProgress, caveExitDismissed, caveMissionIndices]);

  const showDesertFairPrompt = useMemo(() => {
    if (exploreSurface !== 'desert' || !missionProgress) return false;
    const ci = missionProgress.completedIndices;
    if (!Array.isArray(ci)) return false;
    return desertMissionIndices.every((idx) => ci.includes(idx));
  }, [exploreSurface, missionProgress, desertMissionIndices]);

  const showFairLavenderPrompt = useMemo(() => {
    if (exploreSurface !== 'fair' || !missionProgress) return false;
    const ci = missionProgress.completedIndices;
    if (!Array.isArray(ci)) return false;
    return fairMissionIndices.every((idx) => ci.includes(idx));
  }, [exploreSurface, missionProgress, fairMissionIndices]);

  const showLavenderTreetopPrompt = useMemo(() => {
    if (exploreSurface !== 'lavender' || !missionProgress) return false;
    const ci = missionProgress.completedIndices;
    if (!Array.isArray(ci)) return false;
    return lavenderMissionIndices.every((idx) => ci.includes(idx));
  }, [exploreSurface, missionProgress, lavenderMissionIndices]);

  const showTreetopBoatPrompt = useMemo(() => {
    if (exploreSurface !== 'treetop' || !missionProgress) return false;
    const ci = missionProgress.completedIndices;
    if (!Array.isArray(ci)) return false;
    return treetopMissionIndices.every((idx) => ci.includes(idx));
  }, [exploreSurface, missionProgress, treetopMissionIndices]);

  const showBoatPixelPastPrompt = useMemo(() => {
    if (exploreSurface !== 'boat' || !missionProgress) return false;
    const ci = missionProgress.completedIndices;
    if (!Array.isArray(ci)) return false;
    return boatMissionIndices.every((idx) => ci.includes(idx));
  }, [exploreSurface, missionProgress, boatMissionIndices]);

  const showPixelPastKendrexPrompt = useMemo(() => {
    if (exploreSurface !== 'pixelpast' || !missionProgress) return false;
    const ci = missionProgress.completedIndices;
    if (!Array.isArray(ci)) return false;
    return pixelPastMissionIndices.every((idx) => ci.includes(idx));
  }, [exploreSurface, missionProgress, pixelPastMissionIndices]);

  const ensureValidToken = useCallback(async () => {
    let token = localStorage.getItem('token');
    const refreshTokenValue = localStorage.getItem('refreshToken');

    if (token && refreshTokenValue) {
      if (!isTokenValid(token) || needsTokenRefresh(token)) {
        token = await refreshToken(refreshTokenValue);
        localStorage.setItem('token', token);
      }
    }

    if (!token) {
      throw new Error('No authentication token available');
    }

    return token;
  }, []);

  const handleMinigameDarknessPercentUpdated = useCallback((percent) => {
    setDarknessPercent(parseExploreUnknownDarknessPercent(percent));
  }, []);

  const ingestExploreUnknownRunPayload = useCallback((data) => {
    if (data == null || data.runId == null) {
      setActiveRunId(null);
      setMissionsCompletedInRun(0);
      setDarknessPercent(0);
      setDarkKnowledgePartsCollected(0);
      setWeakHeartPartsCollected(0);
      setHeartPartsCollected(0);
      setStrongHeartPartsCollected(0);
      setWeakHeartSetsCompleted(0);
      setHeartSetsCompleted(0);
      setStrongHeartSetsCompleted(0);
      setEpGlobalPartsCollected(0);
      setEpGlobalCharges(0);
      setEpZonePartsCollected(0);
      setEpZoneCharges(0);
      setEpTargetPartsCollected(0);
      setEpTargetCharges(0);
      setRewriteExperiencePartsCollected(0);
      setRewriteExperienceCharges(0);
      setPlayerStartingHealth(30);
      setDarknessBargainM3Used(false);
      setDarknessBargainM6Used(false);
      setDarknessBargainM11Used(false);
      setDarknessBargainM19Used(false);
      setDarknessBargainM25Used(false);
      setDarknessBargainM42Used(false);
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, 'arrival');
      } catch {
        /* ignore */
      }
      setExploreSurface('arrival');
      return;
    }
    setActiveRunId(Number(data.runId));
    const missionsCompletedInRunVal = Number(data.missionsCompletedInRun) || 0;
    setMissionsCompletedInRun(missionsCompletedInRunVal);
    setDarknessPercent(parseExploreUnknownDarknessPercent(data.darknessPercent));
    setDarkKnowledgePartsCollected(parseExploreUnknownDarkKnowledgePartsCollected(data.darkKnowledgePartsCollected));
    const heartRun = readHeartRunFromPayload(data);
    setWeakHeartPartsCollected(heartRun.weakHeartPartsCollected);
    setHeartPartsCollected(heartRun.heartPartsCollected);
    setStrongHeartPartsCollected(heartRun.strongHeartPartsCollected);
    setWeakHeartSetsCompleted(heartRun.weakHeartSetsCompleted);
    setHeartSetsCompleted(heartRun.heartSetsCompleted);
    setStrongHeartSetsCompleted(heartRun.strongHeartSetsCompleted);
    const epRun = readEpRunFromPayload(data);
    setEpGlobalPartsCollected(epRun.epGlobalPartsCollected);
    setEpGlobalCharges(epRun.epGlobalCharges);
    setEpZonePartsCollected(epRun.epZonePartsCollected);
    setEpZoneCharges(epRun.epZoneCharges);
    setEpTargetPartsCollected(epRun.epTargetPartsCollected);
    setEpTargetCharges(epRun.epTargetCharges);
    const rewriteRun = readRewriteExperienceRunFromPayload(data);
    setRewriteExperiencePartsCollected(rewriteRun.rewriteExperiencePartsCollected);
    setRewriteExperienceCharges(rewriteRun.rewriteExperienceCharges);
    setPlayerStartingHealth(parseExploreUnknownPlayerStartingHealth(data.playerStartingHealth));
    setDarknessBargainM3Used(parseExploreUnknownBargainUsed(data.darknessBargainM3Used));
    setDarknessBargainM6Used(parseExploreUnknownBargainUsed(data.darknessBargainM6Used));
    setDarknessBargainM11Used(parseExploreUnknownBargainUsed(data.darknessBargainM11Used));
    setDarknessBargainM19Used(parseExploreUnknownBargainUsed(data.darknessBargainM19Used));
    setDarknessBargainM25Used(parseExploreUnknownBargainUsed(data.darknessBargainM25Used));
    setDarknessBargainM42Used(parseExploreUnknownBargainUsed(data.darknessBargainM42Used));

    // New run (0 missions this run): always start on arrival (map 1). sessionStorage can still hold the
    // previous run's surface; the mission-progress effect only forces arrival when already on arrival
    // (see impliesArrivalOnly), so stale cave/desert/fair would leave the player on the wrong map.
    if (missionsCompletedInRunVal === 0) {
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, 'arrival');
      } catch {
        /* ignore */
      }
      setExploreSurface('arrival');
    }
  }, []);

  const ingestLevelingFromProgress = useCallback((data) => {
    if (data.exploreUnknownLevel != null) {
      setExploreUnknownLevel(Number(data.exploreUnknownLevel));
    }
    if (data.exploreUnknownTotalXp != null) {
      setExploreUnknownTotalXp(Number(data.exploreUnknownTotalXp));
    }
    if (data.exploreUnknownXpIntoLevel != null) {
      setExploreUnknownXpIntoLevel(Number(data.exploreUnknownXpIntoLevel));
    }
    if (data.exploreUnknownXpToNext != null) {
      setExploreUnknownXpToNext(Number(data.exploreUnknownXpToNext));
    }
    if (data.exploreUnknownMilestoneBonuses) {
      setExploreUnknownMilestoneBonuses(data.exploreUnknownMilestoneBonuses);
    }
  }, []);

  const syncExploreUnknownRunAfterMinigame = useCallback(
    async ({ winReward, winRewards } = {}) => {
      if (activeRunId == null) {
        return;
      }
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/active-run`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `active-run sync failed (${response.status})`);
      }
      const data = await response.json();
      if (data != null && data.runId != null) {
        ingestExploreUnknownRunPayload(data);
      }
      const xpRewardIds = new Set([
        'extraXp300',
        'extraXp600',
        'extraXp1200',
        'extraXp2000',
        'extraXp4000',
        'extraXp5000',
      ]);
      const needsXpSync =
        (winReward && xpRewardIds.has(winReward.id)) ||
        (Array.isArray(winRewards) && winRewards.some((r) => r && xpRewardIds.has(r.id)));
      if (needsXpSync) {
        const progressResponse = await fetch(`${API_URL}/explore-unknown/progress`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const progressData = await progressResponse.json().catch(() => ({}));
        if (!progressResponse.ok) {
          throw new Error(progressData.error || `Progress sync failed (${progressResponse.status})`);
        }
        ingestLevelingFromProgress(progressData);
      }
    },
    [activeRunId, ensureValidToken, ingestExploreUnknownRunPayload, ingestLevelingFromProgress]
  );

  const {
    resolveError: minigameResolveError,
    showMinigameMarker,
    minigameMarkerPoint,
    minigameMarkerInteractive,
    minigameId: activeMinigameId,
    minigameChallenge,
    minigameModalOpen,
    minigameCompleteBusy,
    minigameCompleteError,
    minigameCompleteOutcome,
    minigameCompleteWinReward,
    minigameCompleteWinRewards,
    minigameCompleteGambleResolution,
    handleMinigameMarkerActivate,
    handleMinigameModalDismiss,
    handleMinigameComplete,
    handleDevStartBirdMinigameTest,
    devMinigameTestBusy,
    devMinigameTestError,
  } = useExploreUnknownMinigameSurface({
    activeRunId,
    exploreSurface,
    sequencePhase,
    nextAvailableMissionIndex: nextSelectableMissionIndex,
    ensureValidToken,
    onDarknessPercentUpdated: handleMinigameDarknessPercentUpdated,
    onRunStateSyncedAfterComplete: syncExploreUnknownRunAfterMinigame,
  });

  const applyContinueSurfaceTransition = useCallback(async (nextSurface) => {
    if (activeRunId == null) {
      throw new Error('Explore Unknown: activeRunId is required for continue-surface transition');
    }
    setContinueSurfaceBusy(true);
    setContinueSurfaceError(null);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/continue-surface`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Continue surface failed (${response.status})`);
      }
      ingestExploreUnknownRunPayload(data);
      playSoundEffect('/sounds/click.mp3');
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, nextSurface);
      } catch {
        /* ignore */
      }
      setExploreSurface(nextSurface);
      setMapRefreshAnimating(true);
      if (mapAnimTimerRef.current) window.clearTimeout(mapAnimTimerRef.current);
      mapAnimTimerRef.current = window.setTimeout(() => {
        setMapRefreshAnimating(false);
        mapAnimTimerRef.current = null;
      }, 1400);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setContinueSurfaceError(msg);
      throw err;
    } finally {
      setContinueSurfaceBusy(false);
    }
  }, [activeRunId, ensureValidToken, ingestExploreUnknownRunPayload]);

  const openSkipWorldDialogIfEligible = useCallback((fromSurface) => {
    const chainIdx = EXPLORE_SURFACE_CHAIN.indexOf(fromSurface);
    if (exploreUnknownLevel < 45 || chainIdx < 0 || chainIdx + 2 >= EXPLORE_SURFACE_CHAIN.length) {
      return false;
    }
    setSkipWorldDialog({
      fromSurface,
      proceedSurface: EXPLORE_SURFACE_CHAIN[chainIdx + 1],
      skippedSurface: EXPLORE_SURFACE_CHAIN[chainIdx + 1],
      skipTargetSurface: EXPLORE_SURFACE_CHAIN[chainIdx + 2],
    });
    return true;
  }, [exploreUnknownLevel]);

  const handleSkipWorldChoice = useCallback(
    async (choice) => {
      if (!skipWorldDialog || activeRunId == null) {
        throw new Error('Explore Unknown: skip world dialog requires active run');
      }
      setSkipWorldBusy(true);
      setContinueSurfaceError(null);
      try {
        if (choice === 'proceed') {
          await applyContinueSurfaceTransition(skipWorldDialog.proceedSurface);
        } else if (choice === 'skip') {
          const token = await ensureValidToken();
          const response = await fetch(`${API_URL}/explore-unknown/skip-surface`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              runId: activeRunId,
              fromSurface: skipWorldDialog.fromSurface,
              targetSurface: skipWorldDialog.skipTargetSurface,
            }),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(data.error || `Skip surface failed (${response.status})`);
          }
          ingestExploreUnknownRunPayload(data);
          if (Array.isArray(data.completedIndices)) {
            setMissionProgress({
              completedIndices: data.completedIndices,
              nextAvailable: data.nextAvailable === undefined ? null : data.nextAvailable,
            });
          }
          ingestLevelingFromProgress(data);
          try {
            sessionStorage.setItem(
              EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY,
              skipWorldDialog.skipTargetSurface
            );
          } catch {
            /* ignore */
          }
          setExploreSurface(skipWorldDialog.skipTargetSurface);
          setMapRefreshAnimating(true);
          if (mapAnimTimerRef.current) window.clearTimeout(mapAnimTimerRef.current);
          mapAnimTimerRef.current = window.setTimeout(() => {
            setMapRefreshAnimating(false);
            mapAnimTimerRef.current = null;
          }, 1400);
        } else {
          throw new Error(`Explore Unknown: invalid skip world choice (${String(choice)})`);
        }
        setSkipWorldDialog(null);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setContinueSurfaceError(msg);
        throw err;
      } finally {
        setSkipWorldBusy(false);
      }
    },
    [
      skipWorldDialog,
      activeRunId,
      applyContinueSurfaceTransition,
      ensureValidToken,
      ingestExploreUnknownRunPayload,
      ingestLevelingFromProgress,
    ]
  );

  useEffect(() => {
    if (!showCaveEntrancePrompt || skipWorldDialog || exploreUnknownLevel < 45) return;
    openSkipWorldDialogIfEligible('arrival');
  }, [
    showCaveEntrancePrompt,
    skipWorldDialog,
    exploreUnknownLevel,
    openSkipWorldDialogIfEligible,
  ]);

  const handleContinueToFair = useCallback(async () => {
    if (continueSurfaceBusy || skipWorldBusy) return;
    if (openSkipWorldDialogIfEligible('desert')) return;
    try {
      await applyContinueSurfaceTransition('fair');
    } catch {
      /* applyContinueSurfaceTransition sets continueSurfaceError */
    }
  }, [continueSurfaceBusy, skipWorldBusy, applyContinueSurfaceTransition, openSkipWorldDialogIfEligible]);

  const handleContinueToLavender = useCallback(async () => {
    if (continueSurfaceBusy || skipWorldBusy) return;
    if (openSkipWorldDialogIfEligible('fair')) return;
    try {
      await applyContinueSurfaceTransition('lavender');
    } catch {
      /* applyContinueSurfaceTransition sets continueSurfaceError */
    }
  }, [continueSurfaceBusy, skipWorldBusy, applyContinueSurfaceTransition, openSkipWorldDialogIfEligible]);

  const handleContinueToTreetop = useCallback(async () => {
    if (continueSurfaceBusy || skipWorldBusy) return;
    if (openSkipWorldDialogIfEligible('lavender')) return;
    try {
      await applyContinueSurfaceTransition('treetop');
    } catch {
      /* applyContinueSurfaceTransition sets continueSurfaceError */
    }
  }, [continueSurfaceBusy, skipWorldBusy, applyContinueSurfaceTransition, openSkipWorldDialogIfEligible]);

  const handleContinueToBoat = useCallback(async () => {
    if (continueSurfaceBusy || skipWorldBusy) return;
    if (openSkipWorldDialogIfEligible('treetop')) return;
    try {
      await applyContinueSurfaceTransition('boat');
    } catch {
      /* applyContinueSurfaceTransition sets continueSurfaceError */
    }
  }, [continueSurfaceBusy, skipWorldBusy, applyContinueSurfaceTransition, openSkipWorldDialogIfEligible]);

  const handleContinueToPixelPast = useCallback(async () => {
    if (continueSurfaceBusy || skipWorldBusy) return;
    if (openSkipWorldDialogIfEligible('boat')) return;
    try {
      await applyContinueSurfaceTransition('pixelpast');
    } catch {
      /* applyContinueSurfaceTransition sets continueSurfaceError */
    }
  }, [continueSurfaceBusy, skipWorldBusy, applyContinueSurfaceTransition, openSkipWorldDialogIfEligible]);

  const handleContinueToKendrex = useCallback(async () => {
    if (continueSurfaceBusy || skipWorldBusy) return;
    if (openSkipWorldDialogIfEligible('pixelpast')) return;
    try {
      await applyContinueSurfaceTransition('kendrex');
    } catch {
      /* applyContinueSurfaceTransition sets continueSurfaceError */
    }
  }, [continueSurfaceBusy, skipWorldBusy, applyContinueSurfaceTransition, openSkipWorldDialogIfEligible]);

  const handleDismissCaveExit = useCallback(async () => {
    try {
      sessionStorage.setItem(EXPLORE_UNKNOWN_CAVE_EXIT_DISMISSED_KEY, '1');
    } catch {
      /* ignore */
    }
    setCaveExitDismissed(true);

    if (!missionProgress || exploreSurface !== 'cave') return;
    const { completedIndices, nextAvailable } = missionProgress;
    if (
      !Array.isArray(completedIndices) ||
      !firstTwoSliceMissionIndices.every((i) => completedIndices.includes(i)) ||
      nextAvailable !== desertFirstMissionIndex
    ) {
      return;
    }
    if (continueSurfaceBusy || skipWorldBusy) return;
    if (activeRunId == null) {
      setContinueSurfaceError('Explore Unknown: active run is required to leave the cave');
      return;
    }
    if (openSkipWorldDialogIfEligible('cave')) {
      return;
    }
    try {
      await applyContinueSurfaceTransition('desert');
    } catch {
      /* applyContinueSurfaceTransition sets continueSurfaceError */
    }
  }, [
    missionProgress,
    exploreSurface,
    firstTwoSliceMissionIndices,
    desertFirstMissionIndex,
    continueSurfaceBusy,
    skipWorldBusy,
    activeRunId,
    applyContinueSurfaceTransition,
    openSkipWorldDialogIfEligible,
  ]);

  const firstMissionIndexForOverlay = useMemo(() => {
    if (exploreSurface === 'kendrex') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount +
        pixelPastMapSliceMissionCount
      );
    }
    if (exploreSurface === 'pixelpast') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount +
        boatMapSliceMissionCount
      );
    }
    if (exploreSurface === 'boat') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount +
        treetopMapSliceMissionCount
      );
    }
    if (exploreSurface === 'treetop') {
      return (
        desertFirstMissionIndex +
        desertMapSliceMissionCount +
        fairMapSliceMissionCount +
        lavenderMapSliceMissionCount
      );
    }
    if (exploreSurface === 'lavender') {
      return desertFirstMissionIndex + desertMapSliceMissionCount + fairMapSliceMissionCount;
    }
    if (exploreSurface === 'fair') {
      return desertFirstMissionIndex + desertMapSliceMissionCount;
    }
    if (exploreSurface === 'desert') return desertFirstMissionIndex;
    if (exploreSurface === 'cave') return mapSliceMissionCount + 1;
    return 1;
  }, [
    exploreSurface,
    desertFirstMissionIndex,
    desertMapSliceMissionCount,
    fairMapSliceMissionCount,
    lavenderMapSliceMissionCount,
    treetopMapSliceMissionCount,
    boatMapSliceMissionCount,
    pixelPastMapSliceMissionCount,
    mapSliceMissionCount,
  ]);

  const darknessBargainTierOnSurface = SURFACE_TO_DARKNESS_BARGAIN_TIER[exploreSurface];

  const darknessBargainMilestoneMission = useMemo(() => {
    switch (darknessBargainTierOnSurface) {
      case 'm3':
        return 3;
      case 'm6':
        return 7;
      case 'm11':
        return 14;
      case 'm19':
        return 19;
      case 'm25':
        return 25;
      case 'm42':
        return 42;
      default:
        return null;
    }
  }, [darknessBargainTierOnSurface]);

  const isDarknessBargainUsedOnSurface = useMemo(() => {
    switch (darknessBargainTierOnSurface) {
      case 'm3':
        return darknessBargainM3Used;
      case 'm6':
        return darknessBargainM6Used;
      case 'm11':
        return darknessBargainM11Used;
      case 'm19':
        return darknessBargainM19Used;
      case 'm25':
        return darknessBargainM25Used;
      case 'm42':
        return darknessBargainM42Used;
      default:
        return true;
    }
  }, [
    darknessBargainTierOnSurface,
    darknessBargainM3Used,
    darknessBargainM6Used,
    darknessBargainM11Used,
    darknessBargainM19Used,
    darknessBargainM25Used,
    darknessBargainM42Used,
  ]);

  /** Local index (0-based) of the mission that unlocks the Rite on this surface (3→2 on arrival, 7→1 on cave, 14→3 on desert, …). */
  const darknessBargainMissionLocalIndex = useMemo(() => {
    if (darknessBargainMilestoneMission == null) return null;
    return darknessBargainMilestoneMission - firstMissionIndexForOverlay;
  }, [darknessBargainMilestoneMission, firstMissionIndexForOverlay]);

  const showDarknessBargainMarker = useMemo(() => {
    if (activeRunId == null || missionProgress == null || darknessBargainMilestoneMission == null) {
      return false;
    }
    if (isDarknessBargainUsedOnSurface) return false;
    const requiredNextMission = darknessBargainMilestoneMission + 1;
    if (missionProgress.nextAvailable !== requiredNextMission) return false;
    const ci = missionProgress.completedIndices;
    if (!Array.isArray(ci) || !ci.includes(darknessBargainMilestoneMission)) return false;
    const li = darknessBargainMissionLocalIndex;
    if (li == null || !Number.isInteger(li) || li < 0) return false;
    if (!Array.isArray(currentMapPoints) || li >= currentMapPoints.length) return false;
    return true;
  }, [
    activeRunId,
    missionProgress,
    darknessBargainMilestoneMission,
    isDarknessBargainUsedOnSurface,
    darknessBargainMissionLocalIndex,
    currentMapPoints,
  ]);

  const darknessBargainAuthoringPoint = useMemo(() => {
    if (!showDarknessBargainMarker) return null;
    if (!Array.isArray(currentMapPoints) || currentMapPoints.length < 1) {
      return null;
    }
    const li = darknessBargainMissionLocalIndex;
    if (li == null || !Number.isInteger(li) || li < 0 || li >= currentMapPoints.length) {
      throw new Error('Explore Unknown: invalid darkness bargain anchor mission index for this map');
    }
    const off = EXPLORE_UNKNOWN_PORTAL_LAYOUT.darknessBargainOffsetBySurface?.[exploreSurface];
    if (!Array.isArray(off) || off.length !== 2) {
      throw new Error(`Explore Unknown: missing darknessBargainOffsetBySurface for surface ${exploreSurface}`);
    }
    const [mx, my] = currentMapPoints[li];
    return [mx + off[0], my + off[1]];
  }, [showDarknessBargainMarker, currentMapPoints, darknessBargainMissionLocalIndex, exploreSurface]);

  const darknessBargainMarkerInteractive = useMemo(
    () =>
      showDarknessBargainMarker &&
      playerStartingHealth >= EXPLORE_UNKNOWN_BARGAIN_MIN_PRIOR_HEALTH,
    [showDarknessBargainMarker, playerStartingHealth]
  );

  const arrivalMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.arrivalMapImageSrc ?? '/images/shadowverse_arrival.jpg';
  const caveMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.caveMapImageSrc ?? '/images/shadowverse_cave.jpg';
  const desertMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.desertMapImageSrc ?? '/images/shadowverse_desert.jpg';
  const fairMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.fairMapImageSrc ?? '/images/shadowverse_fair.jpg';
  const lavenderMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.lavenderMapImageSrc ?? '/images/shadowverse_lavender.jpg';
  const treetopMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.treetopMapImageSrc ?? '/images/treetop.jpg';
  const boatMapSrc = EXPLORE_UNKNOWN_PORTAL_LAYOUT.boatMapImageSrc ?? '/images/boat.jpg';
  const pixelPastMapSrc =
    EXPLORE_UNKNOWN_PORTAL_LAYOUT.pixelPastMapImageSrc ?? '/images/shadowverse_pixelpast.jpg';
  const kendrexMapSrc =
    EXPLORE_UNKNOWN_PORTAL_LAYOUT.kendrexMapImageSrc ?? '/images/shadowverse_kendrex.jpg';
  const activeMapImageSrc =
    exploreSurface === 'kendrex'
      ? kendrexMapSrc
      : exploreSurface === 'pixelpast'
      ? pixelPastMapSrc
      : exploreSurface === 'boat'
      ? boatMapSrc
      : exploreSurface === 'treetop'
      ? treetopMapSrc
      : exploreSurface === 'lavender'
      ? lavenderMapSrc
      : exploreSurface === 'fair'
        ? fairMapSrc
        : exploreSurface === 'desert'
          ? desertMapSrc
          : exploreSurface === 'cave'
            ? caveMapSrc
            : arrivalMapSrc;

  useEffect(() => {
    setMapImageNaturalSize(null);
  }, [activeMapImageSrc]);

  const activeMapDisplayName = useMemo(() => {
    const leafKey = EXPLORE_UNKNOWN_MAP_NAME_I18N_KEYS[exploreSurface];
    if (!leafKey) {
      throw new Error(`Explore Unknown: unknown surface for map name (${String(exploreSurface)})`);
    }
    return t(`exploreUnknownPage.${leafKey}`);
  }, [exploreSurface, t]);

  const exploreUnknownXpBarPercentValue = useMemo(
    () => exploreUnknownXpBarPercent(exploreUnknownXpIntoLevel, exploreUnknownXpToNext),
    [exploreUnknownXpIntoLevel, exploreUnknownXpToNext]
  );

  const exploreUnknownTotalXpDisplay = useMemo(
    () => Number(exploreUnknownTotalXp || 0).toLocaleString(),
    [exploreUnknownTotalXp]
  );

  const exploreUnknownAtMaxLevel =
    exploreUnknownLevel >= 100 || Number(exploreUnknownXpToNext) <= 0;

  const handleDarknessBargainMarkerActivate = useCallback(() => {
    if (!darknessBargainMarkerInteractive || darknessBargainTierOnSurface == null) return;
    setDarknessBargainPurchaseError(null);
    setDarknessBargainModalTier(darknessBargainTierOnSurface);
  }, [darknessBargainMarkerInteractive, darknessBargainTierOnSurface]);

  const handleDarknessBargainModalDismiss = useCallback(() => {
    if (darknessBargainPurchaseBusy) return;
    setDarknessBargainModalTier(null);
    setDarknessBargainPurchaseError(null);
  }, [darknessBargainPurchaseBusy]);

  const handleConfirmDarknessBargainPurchase = useCallback(async () => {
    if (darknessBargainModalTier == null || activeRunId == null) return;
    setDarknessBargainPurchaseBusy(true);
    setDarknessBargainPurchaseError(null);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/darkness-bargain`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId, tier: darknessBargainModalTier }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Darkness bargain failed (${response.status})`);
      }
      setDarknessPercent(parseExploreUnknownDarknessPercent(data.darknessPercent));
      setPlayerStartingHealth(parseExploreUnknownPlayerStartingHealth(data.playerStartingHealth));
      setDarknessBargainM3Used(parseExploreUnknownBargainUsed(data.darknessBargainM3Used));
      setDarknessBargainM6Used(parseExploreUnknownBargainUsed(data.darknessBargainM6Used));
      setDarknessBargainM11Used(parseExploreUnknownBargainUsed(data.darknessBargainM11Used));
      setDarknessBargainM19Used(parseExploreUnknownBargainUsed(data.darknessBargainM19Used));
      setDarknessBargainM25Used(parseExploreUnknownBargainUsed(data.darknessBargainM25Used));
      setDarknessBargainM42Used(parseExploreUnknownBargainUsed(data.darknessBargainM42Used));
      setDarkKnowledgePartsCollected(parseExploreUnknownDarkKnowledgePartsCollected(data.darkKnowledgePartsCollected));
      const bargainHeartRun = readHeartRunFromPayload(data);
      setWeakHeartPartsCollected(bargainHeartRun.weakHeartPartsCollected);
      setHeartPartsCollected(bargainHeartRun.heartPartsCollected);
      setStrongHeartPartsCollected(bargainHeartRun.strongHeartPartsCollected);
      setWeakHeartSetsCompleted(bargainHeartRun.weakHeartSetsCompleted);
      setHeartSetsCompleted(bargainHeartRun.heartSetsCompleted);
      setStrongHeartSetsCompleted(bargainHeartRun.strongHeartSetsCompleted);
      const bargainEpRun = readEpRunFromPayload(data);
      setEpGlobalPartsCollected(bargainEpRun.epGlobalPartsCollected);
      setEpGlobalCharges(bargainEpRun.epGlobalCharges);
      setEpZonePartsCollected(bargainEpRun.epZonePartsCollected);
      setEpZoneCharges(bargainEpRun.epZoneCharges);
      setEpTargetPartsCollected(bargainEpRun.epTargetPartsCollected);
      setEpTargetCharges(bargainEpRun.epTargetCharges);
      const bargainRewriteRun = readRewriteExperienceRunFromPayload(data);
      setRewriteExperiencePartsCollected(bargainRewriteRun.rewriteExperiencePartsCollected);
      setRewriteExperienceCharges(bargainRewriteRun.rewriteExperienceCharges);
      setDarknessBargainModalTier(null);
    } catch (err) {
      setDarknessBargainPurchaseError(err instanceof Error ? err.message : String(err));
    } finally {
      setDarknessBargainPurchaseBusy(false);
    }
  }, [darknessBargainModalTier, activeRunId, ensureValidToken]);

  useEffect(() => {
    if (darknessBargainModalTier == null) {
      return undefined;
    }
    if (typeof document !== 'undefined' && document.hidden) {
      return undefined;
    }
    const v = getEffectiveSoundOutputVolume(1);
    if (v === null) {
      return undefined;
    }
    const audio = new Audio('/sounds/rite.mp3');
    audio.loop = true;
    audio.volume = v;
    const p = audio.play();
    if (p !== undefined) {
      p.catch(() => {
        /* Autoplay or decode policy — ignore */
      });
    }
    return () => {
      audio.pause();
      audio.currentTime = 0;
      audio.removeAttribute('src');
      audio.load();
    };
  }, [darknessBargainModalTier]);

  useEffect(() => {
    if (sequencePhase !== 'arrival' || nextSelectableMissionIndex == null) {
      setNextMissionCompletionRates(null);
      setNextMissionCompletionRatesError(null);
      setNextMissionCompletionRatesLoading(false);
      return undefined;
    }
    let cancelled = false;
    const missionIndex = nextSelectableMissionIndex;
    (async () => {
      setNextMissionCompletionRatesLoading(true);
      setNextMissionCompletionRatesError(null);
      setNextMissionCompletionRates(null);
      try {
        const token = await ensureValidToken();
        if (!token) {
          throw new Error('Explore Unknown mission completion rates: authentication token is required');
        }
        const response = await fetch(
          `${API_URL}/explore-unknown/mission-win-rates?indices=${encodeURIComponent(String(missionIndex))}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Failed to load mission completion rates (${response.status})`);
        }
        if (cancelled) return;
        const entry = data.missions?.[String(missionIndex)];
        if (!entry || typeof entry !== 'object') {
          throw new Error(
            `Explore Unknown mission completion rates: missing entry for mission ${missionIndex}`
          );
        }
        setNextMissionCompletionRates(entry);
      } catch (err) {
        if (!cancelled) {
          setNextMissionCompletionRatesError(
            err instanceof Error ? err.message : t('exploreUnknownPage.missionCompletionRatesLoadError')
          );
        }
      } finally {
        if (!cancelled) {
          setNextMissionCompletionRatesLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sequencePhase, nextSelectableMissionIndex, ensureValidToken, t]);

  useLayoutEffect(() => {
    const st = location.state;
    if (!st?.resumeArrival || resumeHandledRef.current) return;
    resumeHandledRef.current = true;
    const sid = st.exploreUnknownCommittedStrategyId;
    if (sid == null || sid === '') {
      throw new Error('Explore Unknown: resumeArrival requires exploreUnknownCommittedStrategyId');
    }
    setCommittedStrategyId(String(sid));
    setSelectedStrategyId(String(sid));
    if (st.exploreUnknownRunId != null && st.exploreUnknownRunId !== '') {
      pendingResumeRunIdRef.current = Number(st.exploreUnknownRunId);
    }
    setSequencePhase('arrival');
    setPendingActiveRunCheck(false);
    if (st.exploreUnknownMapRefresh) {
      setMapRefreshAnimating(true);
      if (mapAnimTimerRef.current) window.clearTimeout(mapAnimTimerRef.current);
      mapAnimTimerRef.current = window.setTimeout(() => {
        setMapRefreshAnimating(false);
        mapAnimTimerRef.current = null;
      }, 1400);
    }
    if (backgroundVideoRef.current) {
      backgroundVideoRef.current.pause();
    }
    navigate('/explore-unknown', { replace: true });
  }, [location.state, navigate]);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    const load = async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/active-run`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
        });
        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `active-run failed (${response.status})`);
        }
        const data = await response.json();
        if (cancelled) return;
        if (data != null && data.runId != null && data.strategyId != null) {
          const sid = String(data.strategyId);
          setCommittedStrategyId(sid);
          setSelectedStrategyId(sid);
          ingestExploreUnknownRunPayload(data);
          setRunHydratedFromApi(true);
          setSequencePhase('arrival');
          setRunSessionError(null);
          if (backgroundVideoRef.current) {
            backgroundVideoRef.current.pause();
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Explore Unknown active-run:', err);
      } finally {
        if (!cancelled) {
          setPendingActiveRunCheck(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [ensureValidToken, ingestExploreUnknownRunPayload]);

  useEffect(() => {
    if (sequencePhase !== 'arrival' || !committedStrategyId) return;
    if (runHydratedFromApi) return;
    const ac = new AbortController();
    let cancelled = false;
    const load = async () => {
      setRunSessionError(null);
      try {
        const token = await ensureValidToken();
        const resumeId = pendingResumeRunIdRef.current;
        pendingResumeRunIdRef.current = null;
        const body =
          resumeId != null && !Number.isNaN(resumeId)
            ? { resumeRunId: resumeId }
            : { strategyId: committedStrategyId };
        const response = await fetch(`${API_URL}/explore-unknown/run-session`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
          signal: ac.signal,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Run session failed (${response.status})`);
        }
        if (data.runId == null) {
          throw new Error('Invalid run session response');
        }
        if (!cancelled) {
          ingestExploreUnknownRunPayload(data);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Explore Unknown run session:', err);
        if (!cancelled) {
          setRunSessionError(err.message || 'Run session failed');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [sequencePhase, committedStrategyId, ensureValidToken, runHydratedFromApi, ingestExploreUnknownRunPayload]);

  useEffect(() => {
    if (mapRefreshAnimating) {
      mapRefreshWasAnimatingRef.current = true;
      return;
    }
    if (!mapRefreshWasAnimatingRef.current || sequencePhase !== 'arrival') {
      return;
    }
    mapRefreshWasAnimatingRef.current = false;
    let cancelled = false;
    const sync = async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/active-run`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `active-run sync failed (${response.status})`);
        }
        const data = await response.json();
        if (cancelled) return;
        ingestExploreUnknownRunPayload(data == null || data.runId == null ? null : data);
      } catch (err) {
        console.error('Explore Unknown active-run sync after map refresh:', err);
      }
    };
    sync();
    return () => {
      cancelled = true;
    };
  }, [mapRefreshAnimating, sequencePhase, ensureValidToken, ingestExploreUnknownRunPayload]);

  useEffect(() => {
    return () => {
      if (mapAnimTimerRef.current) window.clearTimeout(mapAnimTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/my-strategies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(`Failed to load strategies (${response.status})`);
        }
        const data = await response.json();
        if (!Array.isArray(data)) {
          throw new Error('Invalid strategies response');
        }
        const validStrategies = data.filter((strategy) => isValidOrderCount(strategy));
        const strategyIdsForValidation = validStrategies
          .filter((strategy) => strategy.id != null)
          .map((strategy) => String(strategy.id));

        const validationResults = await Promise.allSettled(
          strategyIdsForValidation.map(async (idKey) => {
            try {
              const currentToken = await ensureValidToken();
              const validationRes = await fetchJsonWithTimeout(
                `${API_URL}/strategy/${idKey}/validate`,
                { headers: { Authorization: `Bearer ${currentToken}` } },
                STRATEGY_VALIDATION_TIMEOUT_MS
              );
              const rawBody = await validationRes.json().catch(() => ({}));
              const normalized = normalizeStrategyValidationResponse(validationRes, rawBody);
              return [idKey, normalized];
            } catch (err) {
              return [
                idKey,
                {
                  valid: false,
                  insufficientOrders: [],
                  fetchError: true,
                  errorMessage:
                    err?.name === 'AbortError'
                      ? 'Strategy ownership check timed out. Please try again.'
                      : err instanceof Error
                        ? err.message
                        : String(err),
                },
              ];
            }
          })
        );

        const validationEntries = validationResults.map((result, idx) => {
          if (result.status === 'fulfilled') {
            return result.value;
          }
          const idKey = strategyIdsForValidation[idx];
          return [
            idKey,
            {
              valid: false,
              insufficientOrders: [],
              fetchError: true,
              errorMessage:
                result.reason instanceof Error
                  ? result.reason.message
                  : String(result.reason || 'Failed to validate strategy ownership'),
            },
          ];
        });
        if (!cancelled) {
          setStrategies(validStrategies);
          setStrategyValidations(Object.fromEntries(validationEntries));
          setStrategiesError(null);
        }
      } catch (err) {
        console.error('ExploreUnknown strategies:', err);
        if (!cancelled) {
          setStrategies([]);
          setStrategyValidations({});
          setStrategiesError(err.message || 'Failed to load strategies');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [ensureValidToken]);

  useEffect(() => {
    if (pendingActiveRunCheck) return;
    if (!user) return;
    if (!shouldShowTutorial('explore_unknown')) return;
    if (sequencePhase === 'scanner') return;
    const timer = setTimeout(() => setShowTutorialExploreUnknown(true), 600);
    return () => clearTimeout(timer);
  }, [user, shouldShowTutorial, sequencePhase, pendingActiveRunCheck]);

  useEffect(() => {
    const needsLevelingOnly = sequencePhase === null;
    const needsMapProgress = sequencePhase === 'arrival' && mapSliceMissionCount >= 1;
    if (!needsLevelingOnly && !needsMapProgress) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/progress`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Progress failed (${response.status})`);
        }
        if (cancelled) return;
        ingestLevelingFromProgress(data);
        if (needsMapProgress) {
          if (!data || !Array.isArray(data.completedIndices)) {
            throw new Error('Invalid explore-unknown progress response');
          }
          setMissionProgress({
            completedIndices: data.completedIndices,
            nextAvailable: data.nextAvailable === undefined ? null : data.nextAvailable,
          });
          setMissionProgressError(null);
        }
      } catch (err) {
        console.error('Explore Unknown progress:', err);
        if (!cancelled && needsMapProgress) {
          setMissionProgress(null);
          setMissionProgressError(err.message || 'Failed to load mission progress');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    sequencePhase,
    mapSliceMissionCount,
    ensureValidToken,
    activeRunId,
    missionsCompletedInRun,
    ingestLevelingFromProgress,
  ]);

  useEffect(() => {
    const el = backgroundVideoRef.current;
    if (!el) return;
    el.muted = true;
    el.defaultMuted = true;
    el.playsInline = true;
    el.setAttribute('playsinline', '');
    el.setAttribute('webkit-playsinline', '');
    if (sequencePhase === 'arrival' || sequencePhase === 'scanner') {
      el.pause();
      return;
    }
    if (sequencePhase === null && portalBackgroundVideoHoverMode) {
      el.pause();
      return;
    }
    if (sequencePhase === null) {
      const p = el.play();
      if (p !== undefined) {
        p.catch((err) => {
          console.error('Explore Unknown background video: play() failed (portal intro, touch / coarse pointer)', err);
        });
      }
    }
  }, [sequencePhase, portalBackgroundVideoHoverMode]);

  useEffect(() => {
    if (!portalBackgroundVideoHoverMode) return undefined;
    if (pendingActiveRunCheck) return undefined;
    const root = portalRegionRef.current;
    if (!root) {
      throw new Error('Explore Unknown: portalRegionRef is not set while registering portal hover video listeners');
    }

    const playIfPortalIntro = () => {
      if (sequencePhaseRef.current !== null) return;
      const v = backgroundVideoRef.current;
      if (!v) {
        throw new Error('Explore Unknown: background video element missing on portal hover play');
      }
      const p = v.play();
      if (p !== undefined) {
        p.catch((err) => {
          console.error('Explore Unknown background video: play() failed (portal hover / focus)', err);
        });
      }
    };

    const pauseIfPortalIntro = () => {
      if (sequencePhaseRef.current !== null) return;
      const v = backgroundVideoRef.current;
      if (!v) return;
      v.pause();
    };

    const onFocusOut = (e) => {
      if (sequencePhaseRef.current !== null) return;
      const related = e.relatedTarget;
      if (related instanceof Node && root.contains(related)) return;
      pauseIfPortalIntro();
    };

    root.addEventListener('mouseenter', playIfPortalIntro);
    root.addEventListener('mouseleave', pauseIfPortalIntro);
    root.addEventListener('focusin', playIfPortalIntro);
    root.addEventListener('focusout', onFocusOut);
    return () => {
      root.removeEventListener('mouseenter', playIfPortalIntro);
      root.removeEventListener('mouseleave', pauseIfPortalIntro);
      root.removeEventListener('focusin', playIfPortalIntro);
      root.removeEventListener('focusout', onFocusOut);
    };
  }, [portalBackgroundVideoHoverMode, pendingActiveRunCheck]);

  useEffect(() => {
    if (sequencePhase !== 'scanner' || !scannerVideoRef.current) return;
    const v = scannerVideoRef.current;
    v.muted = false;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.play().catch(() => {});
  }, [sequencePhase]);

  useEffect(() => {
    return () => {
      if (strategyHintTimerRef.current !== null) {
        window.clearTimeout(strategyHintTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      try {
        if (exploreWs) exploreWs.close();
      } catch {
        /* ignore */
      }
    };
  }, [exploreWs]);

  const handlePortalClick = (event) => {
    if (sequencePhase !== null) return;
    if (hasEnteredPortal) return;
    if (event.target.closest('.explore-unknown-strategy-dropdown-wrap')) return;

    if (!selectedStrategyId) {
      setStrategyHintVisible(true);
      if (strategyHintTimerRef.current !== null) {
        window.clearTimeout(strategyHintTimerRef.current);
      }
      strategyHintTimerRef.current = window.setTimeout(() => {
        setStrategyHintVisible(false);
        strategyHintTimerRef.current = null;
      }, 3200);
      return;
    }

    // Fresh run should always start on arrival; clear any stale surface from a previous run
    // before scanner/arrival render to avoid a transient wrong-map frame and accidental +5% transition.
    try {
      sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, 'arrival');
    } catch {
      /* ignore */
    }
    setExploreSurface('arrival');

    playSoundEffect('/sounds/click.mp3');
    setCommittedStrategyId(selectedStrategyId);
    if (backgroundVideoRef.current) {
      backgroundVideoRef.current.pause();
    }
    setSequencePhase('scanner');
  };

  const dismissScannerIntro = useCallback(() => {
    const v = scannerVideoRef.current;
    if (v) {
      v.pause();
    }
    setSequencePhase('arrival');
  }, []);

  const handleScannerEnded = () => {
    dismissScannerIntro();
  };

  useEffect(() => {
    if (sequencePhase !== 'scanner') return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      dismissScannerIntro();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [sequencePhase, dismissScannerIntro]);

  const handleScannerError = () => {
    throw new Error('scanner_drone.mp4 failed to load or play');
  };

  const handleMapImageError = () => {
    throw new Error('Explore Unknown map image failed to load');
  };

  const handleMapImageLoad = useCallback((e) => {
    const img = e.currentTarget;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) {
      throw new Error('Explore Unknown map image has invalid natural dimensions');
    }
    setMapImageNaturalSize({ w, h });
  }, []);

  const handleEnterCave = useCallback(async () => {
    if (continueSurfaceBusy || skipWorldBusy) return;
    if (activeRunId == null) {
      setContinueSurfaceError('Explore Unknown: active run is required to enter the cave');
      return;
    }
    if (openSkipWorldDialogIfEligible('arrival')) {
      return;
    }
    try {
      await applyContinueSurfaceTransition('cave');
    } catch {
      /* applyContinueSurfaceTransition sets continueSurfaceError */
    }
  }, [
    continueSurfaceBusy,
    skipWorldBusy,
    activeRunId,
    applyContinueSurfaceTransition,
    openSkipWorldDialogIfEligible,
  ]);

  const handleWaypointAuthoringChange = useCallback(
    (index, ax, ay) => {
      if (process.env.NODE_ENV !== 'development') {
        throw new Error('Explore Unknown waypoint authoring change is development-only');
      }
      const axr = Math.round(ax);
      const ayr = Math.round(ay);
      setDevWaypointOverride((prev) => {
        const base = prev ?? layoutMapPoints.map((p) => [...p]);
        const next = [...base];
        next[index] = [axr, ayr];
        saveDevWaypoints(exploreSurface, next);
        return next;
      });
    },
    [exploreSurface, layoutMapPoints]
  );

  const handleResetDevWaypoints = useCallback(() => {
    try {
      clearDevWaypoints(exploreSurface);
      setDevWaypointOverride(null);
      setDevWaypointUiError(null);
    } catch (err) {
      setDevWaypointUiError(err instanceof Error ? err.message : String(err));
    }
  }, [exploreSurface]);

  const handleCopyDevWaypoints = useCallback(async () => {
    const key =
      exploreSurface === 'kendrex'
        ? 'kendrexPointsOfInterest'
        : exploreSurface === 'pixelpast'
        ? 'pixelPastPointsOfInterest'
        : exploreSurface === 'boat'
        ? 'boatPointsOfInterest'
        : exploreSurface === 'treetop'
        ? 'treetopPointsOfInterest'
        : exploreSurface === 'lavender'
        ? 'lavenderPointsOfInterest'
        : exploreSurface === 'fair'
          ? 'fairPointsOfInterest'
          : exploreSurface === 'desert'
            ? 'desertPointsOfInterest'
            : exploreSurface === 'cave'
              ? 'cavePointsOfInterest'
              : 'arrivalPointsOfInterest';
    const line = `  ${key}: ${JSON.stringify(currentMapPoints)},`;
    if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
      throw new Error('Explore Unknown: clipboard API is not available');
    }
    await navigator.clipboard.writeText(line);
    setDevWaypointUiError(null);
    setWaypointCopyDone(true);
    window.setTimeout(() => setWaypointCopyDone(false), 2000);
  }, [exploreSurface, currentMapPoints]);

  const handleDevAutowinNext = useCallback(async () => {
    if (activeRunId == null) {
      throw new Error('Explore Unknown dev autowin: active run is required');
    }
    setDevAutowinError(null);
    setDevAutowinBusy(true);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/dev-autowin-next`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Dev autowin failed (${response.status})`);
      }
      if (!Array.isArray(data.completedIndices)) {
        throw new Error('Invalid dev autowin response: completedIndices');
      }
      setMissionProgress({
        completedIndices: data.completedIndices,
        nextAvailable: data.nextAvailable === undefined ? null : data.nextAvailable,
      });
      if (data.runEndedByDarkness === true) {
        ingestExploreUnknownRunPayload(null);
        setRunHydratedFromApi(true);
      } else {
        ingestExploreUnknownRunPayload({
          runId: activeRunId,
          missionsCompletedInRun: data.missionsCompletedInRun,
          darknessPercent: data.darknessPercent,
          playerStartingHealth: data.playerStartingHealth,
          darknessBargainM3Used: data.darknessBargainM3Used,
          darknessBargainM6Used: data.darknessBargainM6Used,
          darknessBargainM11Used: data.darknessBargainM11Used,
          darknessBargainM19Used: data.darknessBargainM19Used,
          darknessBargainM25Used: data.darknessBargainM25Used,
          darknessBargainM42Used: data.darknessBargainM42Used,
          darkKnowledgePartsCollected: data.darkKnowledgePartsCollected,
          weakHeartPartsCollected: data.weakHeartPartsCollected,
          heartPartsCollected: data.heartPartsCollected,
          strongHeartPartsCollected: data.strongHeartPartsCollected,
          weakHeartSetsCompleted: data.weakHeartSetsCompleted,
          heartSetsCompleted: data.heartSetsCompleted,
          strongHeartSetsCompleted: data.strongHeartSetsCompleted,
          epGlobalPartsCollected: data.epGlobalPartsCollected,
          epGlobalCharges: data.epGlobalCharges,
          epZonePartsCollected: data.epZonePartsCollected,
          epZoneCharges: data.epZoneCharges,
          epTargetPartsCollected: data.epTargetPartsCollected,
          epTargetCharges: data.epTargetCharges,
          rewriteExperiencePartsCollected: data.rewriteExperiencePartsCollected,
          rewriteExperienceCharges: data.rewriteExperienceCharges,
        });
      }
      setMissionProgressError(null);
    } catch (err) {
      console.error('Explore Unknown dev autowin:', err);
      setDevAutowinError(err instanceof Error ? err.message : String(err));
    } finally {
      setDevAutowinBusy(false);
    }
  }, [activeRunId, ensureValidToken, ingestExploreUnknownRunPayload]);

  const handleDevForceRunLoss = useCallback(async () => {
    if (activeRunId == null) {
      throw new Error('Explore Unknown dev force loss: active run is required');
    }
    setDevForceLossError(null);
    setDevForceLossBusy(true);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/dev-force-run-loss`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Dev force loss failed (${response.status})`);
      }
      ingestExploreUnknownRunPayload(null);
      setRunHydratedFromApi(true);
    } catch (err) {
      console.error('Explore Unknown dev force loss:', err);
      setDevForceLossError(err instanceof Error ? err.message : String(err));
    } finally {
      setDevForceLossBusy(false);
    }
  }, [activeRunId, ensureValidToken, ingestExploreUnknownRunPayload]);

  useEffect(() => {
    if (continueSurfaceBusy) return;

    // New run: `missions_completed` is 0 but progress fetch can still return the *previous* run's
    // completion list for one frame — do not reconcile surfaces (would call continue-surface and
    // add +5% darkness). Ingest + portal already pin arrival for this case.
    if (missionsCompletedInRun === 0 && activeRunId != null) {
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, 'arrival');
      } catch {
        /* ignore */
      }
      if (exploreSurfaceRef.current !== 'arrival') {
        setExploreSurface('arrival');
      }
      return;
    }

    if (!missionProgress) return;

    const applySurfaceTarget = (nextSurface) => {
      const from = exploreSurfaceRef.current;
      if (skipWorldDialog) return;
      if (activeRunId != null && exploreSurfaceChargesDarkness(from, nextSurface)) {
        const chainIdx = EXPLORE_SURFACE_CHAIN.indexOf(from);
        const expectedProceed =
          chainIdx >= 0 ? EXPLORE_SURFACE_CHAIN[chainIdx + 1] : null;
        if (
          exploreUnknownLevel >= 45 &&
          expectedProceed === nextSurface &&
          chainIdx + 2 < EXPLORE_SURFACE_CHAIN.length
        ) {
          openSkipWorldDialogIfEligible(from);
          return;
        }
        void applyContinueSurfaceTransition(nextSurface).catch((err) => {
          console.error('Explore Unknown mission-progress surface transition:', err);
        });
        return;
      }
      try {
        sessionStorage.setItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY, nextSurface);
      } catch {
        /* ignore */
      }
      setExploreSurface(nextSurface);
    };

    const { completedIndices, nextAvailable } = missionProgress;
    const caveFloor = mapSliceMissionCount + 1;
    const desertFloor = 2 * mapSliceMissionCount + 1;
    const ci = Array.isArray(completedIndices) ? completedIndices : [];

    const allPreDesertDone = firstTwoSliceMissionIndices.every((idx) => ci.includes(idx));
    const allDesertMissionsDone = desertMissionIndices.every((idx) => ci.includes(idx));
    const allFairMissionsDone = fairMissionIndices.every((idx) => ci.includes(idx));

    let storedSurface = null;
    try {
      storedSurface = sessionStorage.getItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
    } catch {
      /* ignore */
    }

    if (storedSurface === 'fair' && !allDesertMissionsDone) {
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      storedSurface = null;
    }

    if (storedSurface === 'fair' && allDesertMissionsDone) {
      applySurfaceTarget('fair');
      return;
    }

    if (storedSurface === 'lavender' && !allFairMissionsDone) {
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      storedSurface = null;
    }

    if (storedSurface === 'lavender' && allFairMissionsDone) {
      applySurfaceTarget('lavender');
      return;
    }

    const allLavenderMissionsDone = lavenderMissionIndices.every((idx) => ci.includes(idx));

    if (storedSurface === 'treetop' && !allLavenderMissionsDone) {
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      storedSurface = null;
    }

    if (storedSurface === 'treetop' && allLavenderMissionsDone) {
      applySurfaceTarget('treetop');
      return;
    }

    const allTreetopMissionsDone = treetopMissionIndices.every((idx) => ci.includes(idx));

    if (storedSurface === 'boat' && !allTreetopMissionsDone) {
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      storedSurface = null;
    }

    if (storedSurface === 'boat' && allTreetopMissionsDone) {
      applySurfaceTarget('boat');
      return;
    }

    const allBoatMissionsDone = boatMissionIndices.every((idx) => ci.includes(idx));

    if (storedSurface === 'pixelpast' && !allBoatMissionsDone) {
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      storedSurface = null;
    }

    if (storedSurface === 'pixelpast' && allBoatMissionsDone) {
      applySurfaceTarget('pixelpast');
      return;
    }

    const allPixelPastMissionsDone = pixelPastMissionIndices.every((idx) => ci.includes(idx));

    if (storedSurface === 'kendrex' && !allPixelPastMissionsDone) {
      try {
        sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      storedSurface = null;
    }

    if (storedSurface === 'kendrex' && allPixelPastMissionsDone) {
      applySurfaceTarget('kendrex');
      return;
    }

    if (storedSurface === 'desert') {
      const desertAllowed =
        ci.some((idx) => idx >= desertFloor) ||
        (nextAvailable != null && nextAvailable > desertFloor) ||
        (allPreDesertDone && nextAvailable === desertFloor);
      if (!desertAllowed) {
        try {
          sessionStorage.removeItem(EXPLORE_UNKNOWN_SURFACE_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        storedSurface = null;
      }
    }

    if (ci.some((idx) => idx >= desertFloor) || (nextAvailable != null && nextAvailable > desertFloor)) {
      applySurfaceTarget('desert');
      return;
    }

    if (
      storedSurface === 'desert' &&
      nextAvailable === desertFloor &&
      allPreDesertDone
    ) {
      applySurfaceTarget('desert');
      return;
    }

    // Stay on arrival while next mission is exactly caveFloor so the "Enter cave" prompt can show.
    if (ci.some((idx) => idx >= caveFloor) || (nextAvailable != null && nextAvailable > caveFloor)) {
      applySurfaceTarget('cave');
      return;
    }

    const impliesArrivalOnly =
      !ci.some((idx) => idx >= caveFloor) &&
      (nextAvailable == null || nextAvailable <= mapSliceMissionCount + 1);

    // Only sync down to arrival when the UI is still on arrival. If the player already clicked
    // "Enter" (surface is cave) but the next mission is still the cave floor, impliesArrivalOnly
    // stays true — re-applying arrival would undo that transition when this effect re-runs (e.g.
    // after continue-surface clears `continueSurfaceBusy`).
    if (impliesArrivalOnly && exploreSurfaceRef.current === 'arrival') {
      applySurfaceTarget('arrival');
    }
  }, [
    missionProgress,
    mapSliceMissionCount,
    firstTwoSliceMissionIndices,
    desertMissionIndices,
    fairMissionIndices,
    lavenderMissionIndices,
    treetopMissionIndices,
    boatMissionIndices,
    pixelPastMissionIndices,
    kendrexMissionIndices,
    continueSurfaceBusy,
    activeRunId,
    missionsCompletedInRun,
    applyContinueSurfaceTransition,
    exploreUnknownLevel,
    skipWorldDialog,
    openSkipWorldDialogIfEligible,
  ]);

  const handleAvailableMissionClick = useCallback(
    async (missionIndex, exploreUnknownDevDirectBossMission = false) => {
      setMissionStartError(null);
      if (!committedStrategyId) {
        throw new Error('Explore Unknown: committed strategy is required to start a mission');
      }
      const validation = strategyValidations[committedStrategyId];
      if (!validation) {
        setMissionStartError(t('battleOverview.ownershipStillCheckingBody'));
        return;
      }
      if (validation.valid !== true) {
        if (validation.fetchError) {
          setMissionStartError(validation.errorMessage || t('battleOverview.ownershipCheckFailedBody'));
          return;
        }
        setMissionStartError(t('battleOverview.cannotStartBattleUnowned'));
        return;
      }
      if (exploreWs) return;
      if (startingMission) return;
      if (activeRunId == null) {
        setMissionStartError(t('exploreUnknownPage.runSessionNotReady'));
        return;
      }

      setStartingMission(true);
      let token = await ensureValidToken();
      const refreshTokenValue = localStorage.getItem('refreshToken');
      if (token && refreshTokenValue && (!isTokenValid(token) || needsTokenRefresh(token))) {
        try {
          token = await refreshToken(refreshTokenValue);
          localStorage.setItem('token', token);
        } catch (err) {
          console.error('Explore Unknown mission: token refresh failed', err);
          setMissionStartError(t('battleOverview.tokenRefreshFailed') || String(err.message || err));
          setStartingMission(false);
          return;
        }
      } else if (!token) {
        setMissionStartError(t('battleOverview.noToken') || 'No authentication token.');
        setStartingMission(false);
        return;
      }

      // Same flow as Battle Overview "Rejoin": WebSocket auth → reconnect → use server gameState.
      // If the active session is this Explore Unknown mission for this run, resume instead of start-bot-game.
      const didResumeInPlaceBattle = await new Promise((resolve) => {
        let settled = false;
        let timeoutId = null;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          if (timeoutId != null) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
          }
          resolve(value);
        };
        const probe = new window.WebSocket(WS_URL);
        timeoutId = window.setTimeout(() => {
          try {
            probe.close();
          } catch {
            /* ignore */
          }
          finish(false);
        }, 12000);
        probe.onopen = () => {
          const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
          const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
          probe.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
        };
        probe.onmessage = (event) => {
          let data;
          try {
            data = JSON.parse(event.data);
          } catch {
            return;
          }
          if (data.type === 'auth-success') {
            probe.send(
              JSON.stringify({
                type: 'reconnect',
                token,
              })
            );
          }
          if (data.type === 'reconnect-success') {
            try {
              probe.close();
            } catch {
              /* ignore */
            }
            const gs = data.gameState;
            const winnerPresent =
              gs &&
              gs.winner != null &&
              gs.winner !== '' &&
              !(typeof gs.winner === 'number' && Number.isNaN(gs.winner));
            const sameExploreUnknownSession =
              gs &&
              gs.exploreUnknownMissionIndex != null &&
              gs.exploreUnknownRunId != null &&
              !winnerPresent &&
              Number(gs.exploreUnknownMissionIndex) === Number(missionIndex) &&
              Number(gs.exploreUnknownRunId) === Number(activeRunId);
            if (sameExploreUnknownSession && data.gameId != null && String(data.gameId).trim() !== '') {
              const selectedGeneral = localStorage.getItem('selectedGeneral') || '';
              const selectedAdvisor = localStorage.getItem('selectedAdvisor') || '';
              navigate(`/battle/${data.gameId}`, {
                state: {
                  gameId: data.gameId,
                  gameState: data.gameState,
                  yourTurn: data.yourTurn,
                  selectedGeneral,
                  selectedAdvisor,
                  strategyId: committedStrategyId,
                  userId: localStorage.getItem('userId'),
                  token,
                  isBotGame: true,
                },
              });
              finish(true);
            } else {
              finish(false);
            }
          }
          if (data.type === 'error') {
            try {
              probe.close();
            } catch {
              /* ignore */
            }
            finish(false);
          }
        };
        probe.onerror = () => {
          try {
            probe.close();
          } catch {
            /* ignore */
          }
          finish(false);
        };
      });

      if (didResumeInPlaceBattle) {
        setStartingMission(false);
        return;
      }

      const socket = new window.WebSocket(WS_URL);
      setExploreWs(socket);

      socket.onopen = () => {
        const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
      };

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'auth-success') {
          const startPayload = {
            type: 'start-bot-game',
            strategyId: committedStrategyId,
            exploreUnknownMissionIndex: missionIndex,
            exploreUnknownRunId: activeRunId,
            developerMode: false,
          };
          if (exploreUnknownDevDirectBossMission) {
            startPayload.exploreUnknownDevDirectBossMission = true;
          }
          socket.send(JSON.stringify(startPayload));
        }
        if (data.type === 'match-found') {
          setStartingMission(false);
          try {
            socket.close();
          } catch {
            /* ignore */
          }
          setExploreWs(null);
          const selectedGeneral = localStorage.getItem('selectedGeneral') || '';
          const selectedAdvisor = localStorage.getItem('selectedAdvisor') || '';
          navigate(`/battle/${data.gameId}`, {
            state: {
              strategyId: committedStrategyId,
              userId: localStorage.getItem('userId'),
              token: localStorage.getItem('token'),
              selectedGeneral,
              selectedAdvisor,
              isBotGame: true,
              gameState: data.gameState,
              yourTurn: data.yourTurn,
            },
          });
        }
        if (data.type === 'error') {
          setStartingMission(false);
          setMissionStartError(data.message || t('battleOverview.error'));
          try {
            socket.close();
          } catch {
            /* ignore */
          }
          setExploreWs(null);
        }
      };

      socket.onerror = () => {
        setStartingMission(false);
        setMissionStartError(t('battleOverview.websocketError'));
        setExploreWs(null);
      };

      socket.onclose = () => {
        setExploreWs(null);
      };
    },
    [
      committedStrategyId,
      strategyValidations,
      exploreWs,
      startingMission,
      activeRunId,
      ensureValidToken,
      navigate,
      t,
    ]
  );

  if (pendingActiveRunCheck) {
    return (
      <div className="explore-unknown-viewport-wrapper explore-unknown-bootstrap-loading" aria-busy="true">
        <div className="explore-unknown-bootstrap-loading-inner">{t('exploreUnknownPage.loadingExploreUnknown')}</div>
      </div>
    );
  }

  return (
    <>
    <div className="explore-unknown-viewport-wrapper">
      <video
        ref={backgroundVideoRef}
        className="explore-unknown-fullscreen-background explore-unknown-fullscreen-video"
        preload="auto"
        muted
        loop
        playsInline
        aria-hidden
      >
        <source src="/images/unknown_background.mp4" type="video/mp4" />
      </video>
      <div
        className={`explore-unknown-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="explore-unknown-container" ref={exploreContainerRef}>
          {EXPLORE_UNKNOWN_PORTAL_LAYOUT.showLayoutGrid && sequencePhase === null ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
            />
          ) : null}
          <div className="explore-unknown-page-chrome">
            <FriendsIcon />
            <SettingsMenu
              onLogout={() => {
                onLogout();
                navigate('/');
              }}
            />
            <span className="explore-unknown-page-chrome-username">
              {typeof user === 'string' ? user : (user?.username ?? '')}
            </span>
          </div>

          <button
            type="button"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              navigate('/battleoverview');
            }}
            className="explore-unknown-back-button"
          >
            {t('common.back')}
          </button>

          {sequencePhase === null ? (
            <>
              <ExploreUnknownLevelTree
                level={exploreUnknownLevel}
                xpIntoLevel={exploreUnknownXpIntoLevel}
                xpToNext={exploreUnknownXpToNext}
                totalXp={exploreUnknownTotalXp}
                milestoneBonuses={exploreUnknownMilestoneBonuses}
              />
              {showExploreUnknownDevAutowin ? (
                <ExploreUnknownDevExperiencePanel
                  currentLevel={exploreUnknownLevel}
                  currentTotalXp={exploreUnknownTotalXp}
                  ensureValidToken={ensureValidToken}
                  onApplied={ingestLevelingFromProgress}
                />
              ) : null}
            </>
          ) : null}

          {skipWorldDialog ? (
            <ExploreUnknownSkipWorldDialog
              proceedSurface={skipWorldDialog.proceedSurface}
              skipTargetSurface={skipWorldDialog.skipTargetSurface}
              proceedMapName={t(
                `exploreUnknownPage.${EXPLORE_UNKNOWN_MAP_NAME_I18N_KEYS[skipWorldDialog.proceedSurface]}`
              )}
              skipTargetMapName={t(
                `exploreUnknownPage.${EXPLORE_UNKNOWN_MAP_NAME_I18N_KEYS[skipWorldDialog.skipTargetSurface]}`
              )}
              busy={skipWorldBusy || continueSurfaceBusy}
              onChoose={(choice) => {
                void handleSkipWorldChoice(choice).catch(() => {});
              }}
            />
          ) : null}

          {sequencePhase === null && showPlayer1PortalTutorialButton ? (
            <button
              type="button"
              className="explore-unknown-player1-tutorial-button"
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                setShowTutorialExploreUnknown(true);
              }}
            >
              {t('exploreUnknownPage.replayPortalTutorial')}
            </button>
          ) : null}

          <div
            ref={portalRegionRef}
            className={`explore-unknown-portal${hasEnteredPortal ? ' explore-unknown-portal--entered' : ''}`}
            tabIndex={0}
            role="region"
            aria-labelledby="explore-unknown-heading"
            onClick={handlePortalClick}
            style={{
              left: `${EXPLORE_UNKNOWN_PORTAL_LAYOUT.anchorLeftPercent}%`,
              top: `${EXPLORE_UNKNOWN_PORTAL_LAYOUT.anchorTopPercent}%`,
              width: `${EXPLORE_UNKNOWN_PORTAL_LAYOUT.widthPercent}%`,
              height: `${EXPLORE_UNKNOWN_PORTAL_LAYOUT.heightPercent}%`,
              transform: 'translate(-50%, -50%)',
              ...(EXPLORE_UNKNOWN_PORTAL_LAYOUT.debugOutline
                ? { outline: '2px dashed rgba(0, 255, 200, 0.75)', outlineOffset: '2px' }
                : {}),
            }}
          >
            <div className="explore-unknown-portal-stack">
              <div
                className="explore-unknown-strategy-start-block"
                style={{ transform: `translateX(${strategyStartBlockLogicalLeftPx}px)` }}
              >
                <h1 className="explore-unknown-title" id="explore-unknown-heading">
                  {t('battleOverview.exploreUnknown')}
                </h1>
                <span className="explore-unknown-strategy-label" id="explore-unknown-strategy-label">
                  {t('exploreUnknownPage.strategyLabel')}
                </span>
                <div
                  className="explore-unknown-strategy-dropdown-wrap"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <CustomDropdown
                    options={strategyDropdownOptions}
                    value={selectedStrategyId}
                    onChange={(v) => setSelectedStrategyId(v)}
                    placeholder={t('exploreUnknownPage.strategyPlaceholder')}
                    disabled={hasEnteredPortal}
                    renderOption={(option) => {
                      if (!option?.factionName) {
                        throw new Error('Explore Unknown strategy dropdown: option factionName is required.');
                      }
                      const factionIconPath = getFactionIconPath(option.factionName);
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div
                            className="faction-orb"
                            style={{ background: getFactionOrbBackground(option.factionName) }}
                            title={option.factionName}
                          >
                            {factionIconPath && (
                              <img
                                src={factionIconPath}
                                alt=""
                                className="faction-orb-icon"
                                draggable={false}
                                aria-hidden="true"
                              />
                            )}
                          </div>
                          <span>{option.label}</span>
                        </div>
                      );
                    }}
                  />
                </div>
                {strategiesError ? (
                  <p className="explore-unknown-strategy-error" role="alert">
                    {strategiesError}
                  </p>
                ) : null}
                {!strategiesError && strategies.length === 0 ? (
                  <p className="explore-unknown-strategy-empty">{t('exploreUnknownPage.noStrategies')}</p>
                ) : null}
                {strategyHintVisible ? (
                  <p className="explore-unknown-strategy-hint" role="status">
                    {t('exploreUnknownPage.selectStrategyFirst')}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          {sequencePhase === 'scanner' ? (
            <div
              className="explore-unknown-sequence-overlay explore-unknown-sequence-overlay--scanner-skippable"
              role="presentation"
              onClick={dismissScannerIntro}
            >
              <video
                ref={scannerVideoRef}
                className="explore-unknown-sequence-video"
                playsInline
                controls={false}
                src="/images/scanner_drone.mp4"
                onEnded={handleScannerEnded}
                onError={handleScannerError}
              />
            </div>
          ) : null}
          {sequencePhase === 'arrival' ? (
            <div
              className={`explore-unknown-sequence-overlay${mapRefreshAnimating ? ' explore-unknown-sequence-overlay--map-refresh' : ''}${
                showExploreUnknownDevWaypointEditor &&
                waypointEditMode &&
                mapImageNaturalSize != null &&
                mapImageNaturalSize.w > 0 &&
                mapImageNaturalSize.h > 0
                  ? ' explore-unknown-sequence-overlay--waypoint-edit'
                  : ''
              }`}
              role="img"
              aria-label={`${t('battleOverview.exploreUnknown')}: ${activeMapDisplayName}`}
              data-committed-strategy-id={committedStrategyId ?? undefined}
            >
              <div className="explore-unknown-map-view">
                <img
                  className={`explore-unknown-sequence-image${mapRefreshAnimating ? ' explore-unknown-sequence-image--map-refresh' : ''}`}
                  src={activeMapImageSrc}
                  alt=""
                  onLoad={handleMapImageLoad}
                  onError={handleMapImageError}
                />
                <div className="explore-unknown-map-name-banner">
                  <span className="explore-unknown-map-name">{activeMapDisplayName}</span>
                </div>
                {nextSelectableMissionIndex != null ? (
                  <div
                    className="explore-unknown-next-mission-panel"
                    role="region"
                    aria-label={t('exploreUnknownPage.missionMapPanelTitle', {
                      missionIndex: nextSelectableMissionIndex,
                    })}
                    aria-busy={nextMissionCompletionRatesLoading}
                  >
                    <h2 className="explore-unknown-next-mission-panel-title">
                      {t('exploreUnknownPage.missionMapPanelTitle', {
                        missionIndex: nextSelectableMissionIndex,
                      })}
                    </h2>
                    {nextMissionCompletionRatesError ? (
                      <p className="explore-unknown-next-mission-panel-error" role="alert">
                        {nextMissionCompletionRatesError}
                      </p>
                    ) : null}
                    <dl className="explore-unknown-next-mission-panel-rates">
                      <div className="explore-unknown-next-mission-panel-row">
                        <dt>{t('exploreUnknownPage.missionGlobalCompletionRate')}</dt>
                        <dd>
                          {nextMissionCompletionRatesLoading
                            ? '…'
                            : formatExploreUnknownCompletionRateValue(
                                nextMissionCompletionRates?.global,
                                t
                              )}
                        </dd>
                      </div>
                      <div className="explore-unknown-next-mission-panel-row">
                        <dt>{t('exploreUnknownPage.missionYourCompletionRate')}</dt>
                        <dd>
                          {nextMissionCompletionRatesLoading
                            ? '…'
                            : formatExploreUnknownCompletionRateValue(
                                nextMissionCompletionRates?.individual,
                                t
                              )}
                        </dd>
                      </div>
                    </dl>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          {sequencePhase === 'arrival' &&
          Array.isArray(currentMapPoints) &&
          currentMapPoints.length > 0 &&
          effectiveSlicePointStates ? (
            <ExploreUnknownArrivalPointsOverlay
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              naturalWidth={mapImageNaturalSize?.w}
              naturalHeight={mapImageNaturalSize?.h}
              points={currentMapPoints}
              pointStates={effectiveSlicePointStates}
              firstMissionIndex={firstMissionIndexForOverlay}
              onAvailableMissionClick={handleAvailableMissionClick}
              pathClassName={mapRefreshAnimating ? 'explore-unknown-arrival-poi-overlay--map-refresh' : ''}
              zIndex={
                showExploreUnknownDevWaypointEditor &&
                waypointEditMode &&
                mapImageNaturalSize != null &&
                mapImageNaturalSize.w > 0 &&
                mapImageNaturalSize.h > 0
                  ? 150000
                  : 13100
              }
              waypointEditMode={
                showExploreUnknownDevWaypointEditor &&
                waypointEditMode &&
                mapImageNaturalSize != null &&
                mapImageNaturalSize.w > 0 &&
                mapImageNaturalSize.h > 0
              }
              onWaypointAuthoringChange={handleWaypointAuthoringChange}
              darknessBargainAuthoringPoint={showDarknessBargainMarker ? darknessBargainAuthoringPoint : null}
              darknessBargainVisible={showDarknessBargainMarker}
              darknessBargainPolylineInsertAfterIndex={
                showDarknessBargainMarker && darknessBargainMissionLocalIndex != null
                  ? darknessBargainMissionLocalIndex
                  : null
              }
              darknessBargainInteractive={darknessBargainMarkerInteractive}
              onDarknessBargainClick={handleDarknessBargainMarkerActivate}
              minigameAuthoringPoint={showMinigameMarker ? minigameMarkerPoint : null}
              minigameVisible={showMinigameMarker}
              minigameInteractive={minigameMarkerInteractive}
              onMinigameClick={handleMinigameMarkerActivate}
              bossMissionIndices={EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices}
            />
          ) : null}
          {sequencePhase === 'arrival' && minigameResolveError ? (
            <div className="explore-unknown-minigame-resolve-error" role="alert">
              {minigameResolveError}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && minigameModalOpen && activeMinigameId != null ? (
            <ExploreUnknownMinigameHost
              minigameId={activeMinigameId}
              challenge={minigameChallenge}
              open={minigameModalOpen}
              onDismiss={handleMinigameModalDismiss}
              onPickTree={handleMinigameComplete}
              completeBusy={minigameCompleteBusy}
              completeError={minigameCompleteError}
              completeOutcome={minigameCompleteOutcome}
              completeWinReward={minigameCompleteWinReward}
              completeWinRewards={minigameCompleteWinRewards}
              completeGambleResolution={minigameCompleteGambleResolution}
              showPlayer1DevGrid={showPlayer1MinigameDevGrid}
            />
          ) : null}
          {sequencePhase === 'arrival' && darknessBargainModalTier != null ? (
            <div
              className="explore-unknown-darkness-bargain-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="explore-unknown-darkness-bargain-title"
            >
              <div className="explore-unknown-darkness-bargain-modal">
                <div className="explore-unknown-darkness-bargain-modal-visual">
                  <img
                    src={riteOfRespiteModalBg}
                    alt=""
                    className="explore-unknown-darkness-bargain-modal-image"
                  />
                  <div className="explore-unknown-darkness-bargain-modal-overlay-stack">
                    <div className="explore-unknown-darkness-bargain-modal-overlay-top">
                      <h2 id="explore-unknown-darkness-bargain-title" className="explore-unknown-darkness-bargain-modal-title">
                        {t('exploreUnknownPage.darknessBargainTitle')}
                      </h2>
                    </div>
                    <div className="explore-unknown-darkness-bargain-modal-overlay-bottom">
                      <p className="explore-unknown-darkness-bargain-modal-tagline">
                        {t('exploreUnknownPage.darknessBargainTagline')}
                      </p>
                      {darknessBargainPurchaseError ? (
                        <p className="explore-unknown-darkness-bargain-modal-error" role="alert">
                          {darknessBargainPurchaseError}
                        </p>
                      ) : null}
                      <div className="explore-unknown-darkness-bargain-modal-actions">
                        <button
                          type="button"
                          className="explore-unknown-darkness-bargain-modal-button explore-unknown-darkness-bargain-modal-button--confirm"
                          disabled={darknessBargainPurchaseBusy}
                          onClick={() => handleConfirmDarknessBargainPurchase()}
                        >
                          {darknessBargainPurchaseBusy ? '…' : t('exploreUnknownPage.darknessBargainConfirm')}
                        </button>
                        <button
                          type="button"
                          className="explore-unknown-darkness-bargain-modal-button explore-unknown-darkness-bargain-modal-button--cancel"
                          disabled={darknessBargainPurchaseBusy}
                          onClick={() => handleDarknessBargainModalDismiss()}
                        >
                          {t('exploreUnknownPage.darknessBargainCancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {sequencePhase === 'arrival' &&
          showCaveEntrancePrompt &&
          !skipWorldDialog &&
          exploreUnknownLevel < 45 ? (
            <div className="explore-unknown-cave-entrance" role="region" aria-label={t('exploreUnknownPage.caveEntranceRegion')}>
              <p className="explore-unknown-cave-entrance-text">{t('exploreUnknownPage.caveEntranceBody')}</p>
              <button
                type="button"
                className="explore-unknown-cave-entrance-button"
                onClick={handleEnterCave}
                disabled={continueSurfaceBusy || skipWorldBusy}
              >
                {t('exploreUnknownPage.enterCave')}
              </button>
            </div>
          ) : null}
          {sequencePhase === 'arrival' && showCaveExitPrompt ? (
            <div className="explore-unknown-cave-exit" role="region" aria-label={t('exploreUnknownPage.caveExitRegion')}>
              <p className="explore-unknown-cave-exit-text">{t('exploreUnknownPage.caveExitBody')}</p>
              <button
                type="button"
                className="explore-unknown-cave-exit-button"
                onClick={handleDismissCaveExit}
                disabled={continueSurfaceBusy}
              >
                {t('exploreUnknownPage.caveExitContinue')}
              </button>
            </div>
          ) : null}
          {sequencePhase === 'arrival' && showDesertFairPrompt ? (
            <div
              className="explore-unknown-cave-exit explore-unknown-desert-fair"
              role="region"
              aria-label={t('exploreUnknownPage.desertFairRegion')}
            >
              <p className="explore-unknown-cave-exit-text">{t('exploreUnknownPage.desertFairBody')}</p>
              <button
                type="button"
                className="explore-unknown-cave-exit-button"
                onClick={handleContinueToFair}
                disabled={continueSurfaceBusy}
              >
                {t('exploreUnknownPage.desertFairContinue')}
              </button>
            </div>
          ) : null}
          {sequencePhase === 'arrival' && showFairLavenderPrompt ? (
            <div
              className="explore-unknown-cave-exit explore-unknown-fair-lavender"
              role="region"
              aria-label={t('exploreUnknownPage.fairLavenderRegion')}
            >
              <p className="explore-unknown-cave-exit-text">{t('exploreUnknownPage.fairLavenderBody')}</p>
              <button
                type="button"
                className="explore-unknown-cave-exit-button"
                onClick={handleContinueToLavender}
                disabled={continueSurfaceBusy}
              >
                {t('exploreUnknownPage.fairLavenderContinue')}
              </button>
            </div>
          ) : null}
          {sequencePhase === 'arrival' && showLavenderTreetopPrompt ? (
            <div
              className="explore-unknown-cave-exit explore-unknown-lavender-treetop"
              role="region"
              aria-label={t('exploreUnknownPage.lavenderTreetopRegion')}
            >
              <p className="explore-unknown-cave-exit-text">{t('exploreUnknownPage.lavenderTreetopBody')}</p>
              <button
                type="button"
                className="explore-unknown-cave-exit-button"
                onClick={handleContinueToTreetop}
                disabled={continueSurfaceBusy}
              >
                {t('exploreUnknownPage.lavenderTreetopContinue')}
              </button>
            </div>
          ) : null}
          {sequencePhase === 'arrival' && showTreetopBoatPrompt ? (
            <div
              className="explore-unknown-cave-exit explore-unknown-treetop-boat"
              role="region"
              aria-label={t('exploreUnknownPage.treetopBoatRegion')}
            >
              <p className="explore-unknown-cave-exit-text">{t('exploreUnknownPage.treetopBoatBody')}</p>
              <button
                type="button"
                className="explore-unknown-cave-exit-button"
                onClick={handleContinueToBoat}
                disabled={continueSurfaceBusy}
              >
                {t('exploreUnknownPage.treetopBoatContinue')}
              </button>
            </div>
          ) : null}
          {sequencePhase === 'arrival' && showBoatPixelPastPrompt ? (
            <div
              className="explore-unknown-cave-exit explore-unknown-boat-pixelpast"
              role="region"
              aria-label={t('exploreUnknownPage.boatPixelPastRegion')}
            >
              <p className="explore-unknown-cave-exit-text">{t('exploreUnknownPage.boatPixelPastBody')}</p>
              <button
                type="button"
                className="explore-unknown-cave-exit-button"
                onClick={handleContinueToPixelPast}
                disabled={continueSurfaceBusy}
              >
                {t('exploreUnknownPage.boatPixelPastContinue')}
              </button>
            </div>
          ) : null}
          {sequencePhase === 'arrival' && showPixelPastKendrexPrompt ? (
            <div
              className="explore-unknown-cave-exit explore-unknown-pixelpast-kendrex"
              role="region"
              aria-label={t('exploreUnknownPage.pixelPastKendrexRegion')}
            >
              <p className="explore-unknown-cave-exit-text">{t('exploreUnknownPage.pixelPastKendrexBody')}</p>
              <button
                type="button"
                className="explore-unknown-cave-exit-button"
                onClick={handleContinueToKendrex}
                disabled={continueSurfaceBusy}
              >
                {t('exploreUnknownPage.pixelPastKendrexContinue')}
              </button>
            </div>
          ) : null}
          {sequencePhase === 'arrival' && continueSurfaceError ? (
            <div className="explore-unknown-run-session-error" role="alert">
              {continueSurfaceError}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && runSessionError ? (
            <div className="explore-unknown-run-session-error" role="alert">
              {runSessionError}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && missionProgressError ? (
            <div className="explore-unknown-mission-progress-error" role="alert">
              {missionProgressError}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && activeRunId != null && !runSessionError ? (
            <>
            <div className="explore-unknown-map-run-meters">
              <div
                className="explore-unknown-darkness-bar-wrap explore-unknown-darkness-bar-tooltip-host"
                role="group"
                aria-label={t('exploreUnknownPage.darknessMeterRegion')}
                tabIndex={0}
              >
                <div className="explore-unknown-darkness-tooltip" role="tooltip" aria-hidden="true">
                  <p className="explore-unknown-darkness-tooltip-title">
                    {t('exploreUnknownPage.darknessMeterTooltipTitle', { percent: darknessPercent })}
                  </p>
                </div>
                <img src="/images/sun.png" alt="" className="explore-unknown-darkness-bar-icon" />
                <div className="explore-unknown-darkness-track">
                  <div
                    className="explore-unknown-darkness-fill"
                    style={{ width: `${Math.min(100, Math.max(0, darknessPercent))}%` }}
                  />
                </div>
                <img src="/images/eclipsen.png" alt="" className="explore-unknown-darkness-bar-icon" />
              </div>
              <div
                className="explore-unknown-xp-bar-wrap explore-unknown-xp-bar-tooltip-host"
                role="group"
                aria-label={t('exploreUnknownPage.xpMeterRegion')}
                tabIndex={0}
              >
                <div className="explore-unknown-xp-tooltip" role="tooltip" aria-hidden="true">
                  <p className="explore-unknown-xp-tooltip-title">
                    {t('exploreUnknownPage.xpMeterTooltipTitle', {
                      level: exploreUnknownLevel,
                      xp: exploreUnknownTotalXpDisplay,
                    })}
                  </p>
                  {exploreUnknownAtMaxLevel ? (
                    <p className="explore-unknown-xp-tooltip-body">
                      {t('exploreUnknownPage.xpMeterTooltipMaxLevel')}
                    </p>
                  ) : (
                    <p className="explore-unknown-xp-tooltip-body">
                      {t('exploreUnknownPage.xpMeterTooltipProgress', {
                        current: Number(exploreUnknownXpIntoLevel || 0).toLocaleString(),
                        needed: Number(
                          (exploreUnknownXpIntoLevel || 0) + (exploreUnknownXpToNext || 0)
                        ).toLocaleString(),
                      })}
                    </p>
                  )}
                </div>
                <span className="explore-unknown-xp-bar-label" aria-hidden="true">
                  XP
                </span>
                <div
                  className="explore-unknown-xp-track"
                  role="progressbar"
                  aria-valuenow={exploreUnknownAtMaxLevel ? 100 : exploreUnknownXpBarPercentValue}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={t('exploreUnknownPage.xpMeterTooltipTitle', {
                    level: exploreUnknownLevel,
                    xp: exploreUnknownTotalXpDisplay,
                  })}
                >
                  <div
                    className="explore-unknown-xp-fill"
                    style={{ width: `${exploreUnknownXpBarPercentValue}%` }}
                  />
                </div>
                <span
                  className="explore-unknown-xp-total-slot"
                  title={exploreUnknownTotalXpDisplay}
                  aria-label={t('exploreUnknownPage.levelTree.totalXp', {
                    xp: exploreUnknownTotalXpDisplay,
                  })}
                >
                  <span className="explore-unknown-xp-total">{exploreUnknownTotalXpDisplay}</span>
                </span>
              </div>
            </div>
            <div className="explore-unknown-map-collectible-meters">
              <div
                className="explore-unknown-dark-knowledge-bar-wrap explore-unknown-dark-knowledge-bar-tooltip-host"
                role="group"
                aria-label={t('exploreUnknownBattle.darkKnowledgeMeterRegion')}
                tabIndex={0}
              >
                <div className="explore-unknown-dark-knowledge-tooltip" role="tooltip" aria-hidden="true">
                  <p className="explore-unknown-dark-knowledge-tooltip-title">
                    {t('exploreUnknownBattle.darkKnowledgeMeterTooltipTitle', {
                      collected: darkKnowledgePartsCollected,
                      target: EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS,
                    })}
                  </p>
                  <p className="explore-unknown-dark-knowledge-tooltip-body">
                    {t('exploreUnknownBattle.darkKnowledgeMeterFlashBody', {
                      target: EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS,
                    })}
                  </p>
                </div>
                <img src="/images/dark_knowledge.jpg" alt="" className="explore-unknown-dark-knowledge-bar-icon" />
                <div className="explore-unknown-dark-knowledge-track">
                  <div
                    className="explore-unknown-dark-knowledge-fill"
                    style={{
                      width: `${Math.min(
                        100,
                        (Math.max(0, darkKnowledgePartsCollected) /
                          EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS) *
                          100
                      )}%`,
                    }}
                  />
                </div>
                <span className="explore-unknown-dark-knowledge-count-slot" aria-hidden="true">
                  <span className="explore-unknown-dark-knowledge-count">
                    {darkKnowledgePartsCollected}/{EXPLORE_UNKNOWN_DARK_KNOWLEDGE_TARGET_PARTS}
                  </span>
                </span>
              </div>
              {EXPLORE_UNKNOWN_HEART_METERS.map((meter) => {
                const collected =
                  meter.apiKey === 'weakHeartPartsCollected'
                    ? weakHeartPartsCollected
                    : meter.apiKey === 'heartPartsCollected'
                      ? heartPartsCollected
                      : strongHeartPartsCollected;
                const setsCompleted =
                  meter.apiKeySetsCompleted === 'weakHeartSetsCompleted'
                    ? weakHeartSetsCompleted
                    : meter.apiKeySetsCompleted === 'heartSetsCompleted'
                      ? heartSetsCompleted
                      : strongHeartSetsCompleted;
                const i18nPrefix = `${meter.i18nKey}Meter`;
                return (
                  <ExploreUnknownHeartProgressBar
                    key={meter.id}
                    variant={meter.variant}
                    imageSrc={meter.image}
                    collected={collected}
                    setsCompleted={setsCompleted}
                    targetParts={EXPLORE_UNKNOWN_HEART_TARGET_PARTS}
                    regionLabel={t(`exploreUnknownBattle.${i18nPrefix}Region`)}
                    tooltipTitle={t(`exploreUnknownBattle.${i18nPrefix}TooltipTitle`, {
                      collected,
                      target: EXPLORE_UNKNOWN_HEART_TARGET_PARTS,
                    })}
                    tooltipBody={t(`exploreUnknownBattle.${i18nPrefix}TooltipBody`, {
                      setsCompleted,
                      target: EXPLORE_UNKNOWN_HEART_TARGET_PARTS,
                      healthBonus: meter.healthBonus,
                    })}
                    setsCompletedAriaLabel={t(`exploreUnknownBattle.${i18nPrefix}SetsCompleted`, {
                      count: setsCompleted,
                    })}
                    mode="map"
                  />
                );
              })}
              {EXPLORE_UNKNOWN_EP_METERS.map((meter) => {
                const partsByKey = {
                  epGlobalPartsCollected,
                  epZonePartsCollected,
                  epTargetPartsCollected,
                };
                const chargesByKey = {
                  epGlobalCharges,
                  epZoneCharges,
                  epTargetCharges,
                };
                const collected = partsByKey[meter.apiKeyParts];
                const charges = chargesByKey[meter.apiKeyCharges];
                const i18nPrefix = `${meter.i18nKey}Meter`;
                return (
                  <ExploreUnknownHeartProgressBar
                    key={meter.id}
                    variant={meter.variant}
                    imageSrc={meter.image}
                    collected={collected}
                    charges={charges}
                    targetParts={EXPLORE_UNKNOWN_EP_TARGET_PARTS}
                    regionLabel={t(`exploreUnknownBattle.${i18nPrefix}Region`)}
                    tooltipTitle={t(`exploreUnknownBattle.${i18nPrefix}TooltipTitle`, {
                      collected,
                      target: EXPLORE_UNKNOWN_EP_TARGET_PARTS,
                    })}
                    tooltipBody={t(`exploreUnknownBattle.${i18nPrefix}TooltipBody`, {
                      charges,
                      target: EXPLORE_UNKNOWN_EP_TARGET_PARTS,
                    })}
                    chargesAriaLabel={t(`exploreUnknownBattle.${i18nPrefix}Charges`, { count: charges })}
                    mode="map"
                  />
                );
              })}
              <ExploreUnknownHeartProgressBar
                key={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER.id}
                variant={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER.variant}
                imageSrc={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_METER.image}
                collected={rewriteExperiencePartsCollected}
                charges={rewriteExperienceCharges}
                targetParts={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS}
                regionLabel={t('exploreUnknownBattle.rewriteExperienceMeterRegion')}
                tooltipTitle={t('exploreUnknownBattle.rewriteExperienceMeterTooltipTitle', {
                  collected: rewriteExperiencePartsCollected,
                  target: EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS,
                })}
                tooltipBody={t('exploreUnknownBattle.rewriteExperienceMeterTooltipBody', {
                  charges: rewriteExperienceCharges,
                  target: EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS,
                  darknessPercent: 10,
                })}
                chargesAriaLabel={t('exploreUnknownBattle.rewriteExperienceMeterCharges', {
                  count: rewriteExperienceCharges,
                })}
                mode="map"
              />
            </div>
            </>
          ) : null}
          {sequencePhase === 'arrival' &&
          showExploreUnknownDevAutowin &&
          activeRunId != null &&
          (exploreSurface === 'arrival' ||
            exploreSurface === 'cave' ||
            exploreSurface === 'desert' ||
            exploreSurface === 'fair' ||
            exploreSurface === 'lavender' ||
            exploreSurface === 'treetop') ? (
            <div className="explore-unknown-dev-minigame-test">
              <button
                type="button"
                className="explore-unknown-dev-minigame-test-button"
                disabled={
                  devMinigameTestBusy ||
                  minigameCompleteBusy ||
                  devAutowinBusy ||
                  devForceLossBusy ||
                  startingMission
                }
                onClick={() => handleDevStartBirdMinigameTest()}
              >
                {devMinigameTestBusy
                  ? '…'
                  : exploreSurface === 'cave'
                    ? t('exploreUnknownPage.minigame.wombafleks.devButton')
                    : exploreSurface === 'desert'
                    ? t('exploreUnknownPage.minigame.rewardMemory.devButton')
                    : exploreSurface === 'fair'
                      ? t('exploreUnknownPage.minigame.balloonBooth.devButton')
                      : exploreSurface === 'lavender'
                        ? t('exploreUnknownPage.minigame.theGamble.devButton')
                        : exploreSurface === 'treetop'
                          ? t('exploreUnknownPage.minigame.spottingObjects.devButton')
                          : t('exploreUnknownPage.minigame.beyondBird.devButton')}
              </button>
              {devMinigameTestError ? (
                <p className="explore-unknown-dev-minigame-test-error" role="alert">
                  {devMinigameTestError}
                </p>
              ) : null}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && showExploreUnknownDevAutowin && activeRunId != null ? (
            <div className="explore-unknown-dev-autowin">
              <div className="explore-unknown-dev-boss-jumps" aria-label="Dev: jump to boss mission">
                {(EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices || []).map((bossIdx) => (
                  <button
                    key={`dev-boss-${bossIdx}`}
                    type="button"
                    className="explore-unknown-dev-boss-jump-link"
                    disabled={devAutowinBusy || devForceLossBusy || startingMission}
                    onClick={() => handleAvailableMissionClick(bossIdx, true)}
                  >
                    Boss {bossIdx}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="explore-unknown-dev-autowin-button"
                disabled={devAutowinBusy || devForceLossBusy || startingMission}
                onClick={() => handleDevAutowinNext()}
              >
                {devAutowinBusy ? '…' : 'Dev: autowin next mission'}
              </button>
              <button
                type="button"
                className="explore-unknown-dev-autowin-button explore-unknown-dev-autowin-button--loss"
                disabled={devAutowinBusy || devForceLossBusy || startingMission}
                onClick={() => handleDevForceRunLoss()}
              >
                {devForceLossBusy ? '…' : 'Dev: force run loss'}
              </button>
              {devAutowinError ? (
                <p className="explore-unknown-dev-autowin-error" role="alert">
                  {devAutowinError}
                </p>
              ) : null}
              {devForceLossError ? (
                <p className="explore-unknown-dev-autowin-error" role="alert">
                  {devForceLossError}
                </p>
              ) : null}
            </div>
          ) : null}
          {sequencePhase === 'arrival' && missionStartError ? (
            <div className="explore-unknown-mission-start-error" role="alert">
              {missionStartError}
            </div>
          ) : null}
          {startingMission ? (
            <div className="explore-unknown-starting-mission" role="status">
              {t('exploreUnknownPage.startingMission')}
            </div>
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'arrival' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showArrivalLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13000}
              tooltipFooter={t('exploreUnknownPage.arrivalGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'cave' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showCaveLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.caveGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'desert' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showDesertLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.desertGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'fair' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showFairLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.fairGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'lavender' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showLavenderLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.lavenderGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' &&
          exploreSurface === 'kendrex' &&
          EXPLORE_UNKNOWN_PORTAL_LAYOUT.showKendrexLayoutGrid ? (
            <ExploreUnknownGridOverlay
              containerRef={exploreContainerRef}
              logicalWidth={containerBaseWidth}
              logicalHeight={containerBaseHeight}
              gridSize={EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20}
              isVisible
              zIndex={13200}
              tooltipFooter={t('exploreUnknownPage.kendrexGridHint', {
                gridSize: EXPLORE_UNKNOWN_PORTAL_LAYOUT.gridSize ?? 20,
              })}
            />
          ) : null}
          {sequencePhase === 'arrival' && showExploreUnknownDevWaypointEditor && typeof document !== 'undefined'
            ? createPortal(
                <div className="explore-unknown-dev-waypoint-bar" role="region" aria-label="Development waypoint editor">
                  <span className="explore-unknown-dev-waypoint-bar-title">Dev: waypoints</span>
                  <button
                    type="button"
                    className="explore-unknown-dev-waypoint-bar-button"
                    disabled={mapImageNaturalSize == null}
                    onClick={() => setWaypointEditMode((v) => !v)}
                  >
                    {waypointEditMode ? 'Stop editing' : 'Drag to edit'}
                  </button>
                  <button
                    type="button"
                    className="explore-unknown-dev-waypoint-bar-button"
                    onClick={() => handleResetDevWaypoints()}
                  >
                    Reset to layout file
                  </button>
                  <button
                    type="button"
                    className="explore-unknown-dev-waypoint-bar-button"
                    onClick={() => {
                      void handleCopyDevWaypoints().catch((err) => {
                        setDevWaypointUiError(err instanceof Error ? err.message : String(err));
                      });
                    }}
                  >
                    {waypointCopyDone ? 'Copied' : 'Copy array for layout file'}
                  </button>
                  <span className="explore-unknown-dev-waypoint-bar-surface">
                    {(exploreSurface === 'kendrex'
                      ? 'kendrex'
                      : exploreSurface === 'pixelpast'
                      ? 'pixelpast'
                      : exploreSurface === 'boat'
                      ? 'boat'
                      : exploreSurface === 'treetop'
                      ? 'treetop'
                      : exploreSurface === 'lavender'
                      ? 'lavender'
                      : exploreSurface === 'fair'
                        ? 'fair'
                        : exploreSurface === 'desert'
                          ? 'desert'
                          : exploreSurface === 'cave'
                            ? 'cave'
                            : 'arrival') + ' · localStorage'}
                  </span>
                  {mapImageNaturalSize == null ? (
                    <span className="explore-unknown-dev-waypoint-bar-hint">Wait for map image to load before editing.</span>
                  ) : null}
                  {devWaypointStorageError ? (
                    <span className="explore-unknown-dev-waypoint-bar-error" role="alert">
                      {devWaypointStorageError}
                    </span>
                  ) : null}
                  {devWaypointUiError ? (
                    <span className="explore-unknown-dev-waypoint-bar-error" role="alert">
                      {devWaypointUiError}
                    </span>
                  ) : null}
                </div>,
                document.body
              )
            : null}
        </div>
      </div>
    </div>
    {showTutorialExploreUnknown &&
      createPortal(
        <TutorialMessageBox
          wrapperClassName="explore-unknown-first-visit-tutorial"
          image="/images/tutorial_explore_unknown.png"
          text={t('exploreUnknownPage.portalTutorialText')}
          voiceKey="exploreUnknownPage.portalTutorialText"
          onContinue={async () => {
            const token = localStorage.getItem('token');
            if (token) {
              await fetch(`${API_URL}/tutorial/mark-seen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ messageKey: 'explore_unknown' }),
              });
              markSeenLocally('explore_unknown');
            }
            setShowTutorialExploreUnknown(false);
          }}
        />,
        document.body
      )}
    </>
  );
}

export default ExploreUnknown;
